/* tslint:disable */
/* eslint-disable */
/**
 *
 * @export
 * @interface Account
 */
export interface Account {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Account
   */
  id: string;
  /**
   * Issuer-provided unique id.
   * @type {string}
   * @memberof Account
   */
  externalId: string;
  /**
   * User-friendly account name.
   * @type {string}
   * @memberof Account
   */
  name: string;
  /**
   *
   * @type {CardAccountIssuer}
   * @memberof Account
   */
  issuer: CardAccountIssuer | null;
  /**
   * Name of the institution.
   * @type {string}
   * @memberof Account
   */
  institutionName: string;
  /**
   *
   * @type {AccountConnection}
   * @memberof Account
   */
  connection: AccountConnection;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof Account
   */
  currencyCode: string;
  /**
   * True if account is primarily used to issue virtual cards.
   * @type {boolean}
   * @memberof Account
   */
  isVcs: boolean;
  /**
   * True if account is no longer accessible with the issuer. Could be deleted or otherwise unable to sync via gateway
   * @type {boolean}
   * @memberof Account
   */
  isPlaceholder: boolean;
  /**
   * Account timezone.
   * @type {string}
   * @memberof Account
   */
  tz: string;
  /**
   * Amount of funds available after factoring for authorization holds and pending transactions.
   * @type {number}
   * @memberof Account
   */
  availableBalance: number;
  /**
   * Amount of available credit. Calculated by taking the credit limit minus any outstanding transactions.
   * @type {number}
   * @memberof Account
   */
  availableCredit: number;
  /**
   * Custom data field associated with the account.
   * @type {string}
   * @memberof Account
   */
  customData: string;
  /**
   *
   * @type {AccountCapabilities}
   * @memberof Account
   */
  capabilities: AccountCapabilities;
}
/**
 * Account capabilities.
 * @export
 * @interface AccountCapabilities
 */
export interface AccountCapabilities {
  /**
   *
   * @type {AccountCapabilitiesIssuing}
   * @memberof AccountCapabilities
   */
  issuing?: AccountCapabilitiesIssuing;
  /**
   *
   * @type {AccountCapabilitiesCard}
   * @memberof AccountCapabilities
   */
  card?: AccountCapabilitiesCard;
  /**
   *
   * @type {AccountCapabilitiesVirtualCard}
   * @memberof AccountCapabilities
   */
  virtualCard?: AccountCapabilitiesVirtualCard;
}
/**
 * Physical and/or ghost card capabilities supported for cards issued from the account.
 * @export
 * @interface AccountCapabilitiesCard
 */
export interface AccountCapabilitiesCard {
  /**
   * Statuses supported for an issued card of this type.
   * @type {Array<string>}
   * @memberof AccountCapabilitiesCard
   */
  readonly status?: Array<AccountCapabilitiesCardStatusEnum>;
  /**
   * Issued cards of this type can be added to a digital wallet.
   * @type {boolean}
   * @memberof AccountCapabilitiesCard
   */
  readonly digitalWalletSupport?: boolean;
  /**
   *
   * @type {AccountCapabilitiesCardAllowance}
   * @memberof AccountCapabilitiesCard
   */
  allowance?: AccountCapabilitiesCardAllowance;
  /**
   * Approvals supported for Card.
   * @type {boolean}
   * @memberof AccountCapabilitiesCard
   */
  readonly approvals?: boolean;
}

/**
 * @export
 */
export const AccountCapabilitiesCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled'
} as const;
export type AccountCapabilitiesCardStatusEnum = typeof AccountCapabilitiesCardStatusEnum[keyof typeof AccountCapabilitiesCardStatusEnum];

/**
 * Allowance capabilities supported for an issued card of this type.
 * @export
 * @interface AccountCapabilitiesCardAllowance
 */
export interface AccountCapabilitiesCardAllowance {
  /**
   * Durations for allowance interval that are supported.
   * @type {Array<string>}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly interval?: Array<AccountCapabilitiesCardAllowanceIntervalEnum>;
  /**
   * `interval` and `intervalAmount` allowance fields are supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly intervalAmount?: boolean;
  /**
   * `transactionLimit` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly transactionLimit?: boolean;
  /**
   * `startDate` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly startDate?: boolean;
  /**
   * `endDate` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly endDate?: boolean;
  /**
   * `remainingBalance` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly remainingBalance?: boolean;
  /**
   * `allowedMerchants` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly allowedMerchants?: boolean;
  /**
   * allowedMerchantGroups` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly allowedMerchantGroups?: boolean;
  /**
   * allowedMerchantIds` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly allowedMerchantIds?: boolean;
  /**
   * allowedVendor` allowance field is supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly allowedVendor?: boolean;
  /**
   * How many merchant ids can be selected; 0 if unsupported for this account.
   * @type {number}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly supportedMerchantIdRestrictions?: number;
  /**
   * How many mcc categories can be selected; 0 if unsupported for this account.
   * @type {number}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly supportedMerchantRestrictions?: number;
  /**
   * How many mcc group categories can be selected; 0 if unsupported for this account.
   * @type {number}
   * @memberof AccountCapabilitiesCardAllowance
   */
  readonly supportedMerchantGroupRestrictions?: number;
}

/**
 * @export
 */
export const AccountCapabilitiesCardAllowanceIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type AccountCapabilitiesCardAllowanceIntervalEnum =
  typeof AccountCapabilitiesCardAllowanceIntervalEnum[keyof typeof AccountCapabilitiesCardAllowanceIntervalEnum];

/**
 * Issuing capabilities supported for the account.
 * @export
 * @interface AccountCapabilitiesIssuing
 */
export interface AccountCapabilitiesIssuing {
  /**
   * Physical card issuing supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesIssuing
   */
  readonly physical?: boolean;
  /**
   * Ghost card issuing supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesIssuing
   */
  readonly ghost?: boolean;
  /**
   * Virtual card issuing supported.
   * @type {boolean}
   * @memberof AccountCapabilitiesIssuing
   */
  readonly virtual?: boolean;
}
/**
 * Virtual card capabilities supported for virtual cards issued from the account.
 * @export
 * @interface AccountCapabilitiesVirtualCard
 */
export interface AccountCapabilitiesVirtualCard {
  /**
   * Statuses supported for an issued card of this type.
   * @type {Array<string>}
   * @memberof AccountCapabilitiesVirtualCard
   */
  readonly status?: Array<AccountCapabilitiesVirtualCardStatusEnum>;
  /**
   * Issued cards of this type can be added to a digital wallet.
   * @type {boolean}
   * @memberof AccountCapabilitiesVirtualCard
   */
  readonly digitalWalletSupport?: boolean;
  /**
   *
   * @type {AccountCapabilitiesCardAllowance}
   * @memberof AccountCapabilitiesVirtualCard
   */
  allowance?: AccountCapabilitiesCardAllowance;
  /**
   * Approvals supported for Card.
   * @type {boolean}
   * @memberof AccountCapabilitiesVirtualCard
   */
  readonly approvals?: boolean;
}

/**
 * @export
 */
export const AccountCapabilitiesVirtualCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled'
} as const;
export type AccountCapabilitiesVirtualCardStatusEnum =
  typeof AccountCapabilitiesVirtualCardStatusEnum[keyof typeof AccountCapabilitiesVirtualCardStatusEnum];

/**
 * Connection used to supply data.
 * @export
 * @interface AccountConnection
 */
export interface AccountConnection {
  /**
   * Unique ID.
   * @type {string}
   * @memberof AccountConnection
   */
  readonly id?: string;
  /**
   * Business unit the connection belongs to.
   * @type {string}
   * @memberof AccountConnection
   */
  readonly businessUnit?: string;
  /**
   * Customer that the connection belongs to (business unit specific).
   * @type {string}
   * @memberof AccountConnection
   */
  customerId: string;
  /**
   *
   * @type {object}
   * @memberof AccountConnection
   */
  metadata?: object | null;
  /**
   * Backend that powers the integration.
   * @type {string}
   * @memberof AccountConnection
   */
  feedBackend: AccountConnectionFeedBackendEnum;
  /**
   * Current status.
   * @type {string}
   * @memberof AccountConnection
   */
  status: AccountConnectionStatusEnum;
  /**
   * Accounts that belong to the connection
   * @type {Array<ConnectionDetailAccountsInner>}
   * @memberof AccountConnection
   */
  accounts: Array<ConnectionDetailAccountsInner>;
  /**
   *
   * @type {ConnectionDetailConfiguration}
   * @memberof AccountConnection
   */
  _configuration: ConnectionDetailConfiguration;
}

/**
 * @export
 */
export const AccountConnectionFeedBackendEnum = {
  Simulation: 'simulation',
  Cdf3: 'cdf3',
  EmburseCards: 'emburse_cards',
  AmexFins: 'amex_fins',
  CapitalOne: 'capital_one',
  AmexFiles: 'amex_files',
  AmexCpg: 'amex_cpg',
  VisaFiles: 'visa_files',
  Finicity: 'finicity',
  AmexCel: 'amex_cel',
  EfsFiles: 'efs_files'
} as const;
export type AccountConnectionFeedBackendEnum = typeof AccountConnectionFeedBackendEnum[keyof typeof AccountConnectionFeedBackendEnum];

/**
 * @export
 */
export const AccountConnectionStatusEnum = {
  Active: 'active',
  Disconnected: 'disconnected',
  Pending: 'pending',
  Deactivated: 'deactivated'
} as const;
export type AccountConnectionStatusEnum = typeof AccountConnectionStatusEnum[keyof typeof AccountConnectionStatusEnum];

/**
 *
 * @export
 * @interface Approval
 */
export interface Approval {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof Approval
   */
  interval: ApprovalIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof Approval
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof Approval
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof Approval
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof Approval
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof Approval
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof Approval
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof Approval
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof Approval
   */
  allowedVendor?: string | null;
  /**
   * Available amount remaining on the allowance. A `null` value indicates `remainingBalance` not supported by Issuer.
   * @type {number}
   * @memberof Approval
   */
  readonly remainingBalance?: number | null;
  /**
   * Next date and time when allowance transaction balance will be reset at. This field is based upon the `interval` field. For `continuous` allowance intervals this field will be empty.
   * @type {string}
   * @memberof Approval
   */
  readonly balanceResetsAt?: string | null;
  /**
   * Description for approved requisition. E.g. `October New York Trip`
   * @type {string}
   * @memberof Approval
   */
  description: string;
  /**
   * Optional issuer-provided unique id.
   * @type {string}
   * @memberof Approval
   */
  readonly externalId?: string;
}

/**
 * @export
 */
export const ApprovalIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type ApprovalIntervalEnum = typeof ApprovalIntervalEnum[keyof typeof ApprovalIntervalEnum];

/**
 *
 * @export
 * @interface Authorization
 */
export interface Authorization {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Authorization
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof Authorization
   */
  externalId?: string | null;
  /**
   * A code used to link authorizations and settlements.
   * @type {string}
   * @memberof Authorization
   */
  approvalCode: string;
  /**
   * Approved authorization amount. Negative for debits and positive for credits.
   * @type {number}
   * @memberof Authorization
   */
  authorizedAmount: number;
  /**
   * Authorization date.
   * @type {string}
   * @memberof Authorization
   */
  chargeDate: string;
  /**
   * Authorization time. This field is only provided if both the date and time at which a transaction was authorized are available.
   * @type {string}
   * @memberof Authorization
   */
  chargedAt?: string | null;
  /**
   * ISO 4217 currency code for `authorizedAmount` and `requestedAmount`.
   * @type {string}
   * @memberof Authorization
   */
  currencyCode: string;
  /**
   * Amount in original currency (if different from `currencyCode`).
   * @type {number}
   * @memberof Authorization
   */
  foreignAmount?: number | null;
  /**
   * ISO 4217 currency code for original amount (if different from `currencyCode`).
   * @type {string}
   * @memberof Authorization
   */
  foreignCurrencyCode?: string | null;
  /**
   * Additional description of this authorization.
   * @type {string}
   * @memberof Authorization
   */
  memo: string;
  /**
   *
   * @type {AuthorizationMerchant}
   * @memberof Authorization
   */
  merchant: AuthorizationMerchant;
  /**
   *
   * @type {AuthorizationVendor}
   * @memberof Authorization
   */
  vendor: AuthorizationVendor;
  /**
   * Initial authorization request amount. Negative for debits and positive for credits.
   * @type {number}
   * @memberof Authorization
   */
  requestedAmount: number;
  /**
   * Authorization status.
   * @type {string}
   * @memberof Authorization
   */
  status: AuthorizationStatusEnum;
  /**
   * Description for Authorization status. May only populated if status is declined depending on issuer.
   * @type {string}
   * @memberof Authorization
   */
  statusDescription: string;
  /**
   *
   * @type {AuthorizationCard}
   * @memberof Authorization
   */
  card: AuthorizationCard;
  /**
   * Card network.
   * @type {string}
   * @memberof Authorization
   */
  network: AuthorizationNetworkEnum;
  /**
   *
   * @type {AuthorizationVirtualCard}
   * @memberof Authorization
   */
  virtualCard?: AuthorizationVirtualCard | null;
}

/**
 * @export
 */
export const AuthorizationStatusEnum = {
  Pending: 'pending',
  Closed: 'closed',
  Declined: 'declined'
} as const;
export type AuthorizationStatusEnum = typeof AuthorizationStatusEnum[keyof typeof AuthorizationStatusEnum];

/**
 * @export
 */
export const AuthorizationNetworkEnum = {
  Unknown: 'unknown',
  Amex: 'amex',
  Mastercard: 'mastercard',
  Visa: 'visa',
  Demo: 'demo',
  Discover: 'discover',
  DinersClub: 'diners-club'
} as const;
export type AuthorizationNetworkEnum = typeof AuthorizationNetworkEnum[keyof typeof AuthorizationNetworkEnum];

/**
 * Card that made the authorization
 * @export
 * @interface AuthorizationCard
 */
export interface AuthorizationCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof AuthorizationCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof AuthorizationCard
   */
  externalId?: string | null;
  /**
   *
   * @type {CardDuplicatesInnerCardholder}
   * @memberof AuthorizationCard
   */
  cardholder?: CardDuplicatesInnerCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof AuthorizationCard
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof AuthorizationCard
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof AuthorizationCard
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof AuthorizationCard
   */
  readonly bin?: string | null;
}
/**
 * Merchant data for transaction
 * @export
 * @interface AuthorizationMerchant
 */
export interface AuthorizationMerchant {
  /**
   * Merchant address.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  address?: string | null;
  /**
   * Merchant city.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  city?: string | null;
  /**
   * Merchant country.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  country?: string | null;
  /**
   * Merchant MCC.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  mcc?: string | null;
  /**
   * Merchant MID.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  mid?: string | null;
  /**
   * Merchant name.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  name?: string | null;
  /**
   * Merchant state.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  state?: string | null;
  /**
   * Merchant Tax ID.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  taxId?: string | null;
  /**
   * Merchant zip code.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  zipCode?: string | null;
  /**
   * Generic merchant category details which deviate from standard merchant mcc.
   * @type {string}
   * @memberof AuthorizationMerchant
   */
  categoryDetail?: string | null;
}
/**
 * Vendor data for transaction
 * @export
 * @interface AuthorizationVendor
 */
export interface AuthorizationVendor {
  /**
   * Unique ID.
   * @type {string}
   * @memberof AuthorizationVendor
   */
  id: string;
  /**
   * Vendor name.
   * @type {string}
   * @memberof AuthorizationVendor
   */
  name: string;
  /**
   * Vendor website.
   * @type {string}
   * @memberof AuthorizationVendor
   */
  website: string;
}
/**
 * Virtual card that made the authorization
 * @export
 * @interface AuthorizationVirtualCard
 */
export interface AuthorizationVirtualCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof AuthorizationVirtualCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof AuthorizationVirtualCard
   */
  externalId?: string;
  /**
   *
   * @type {VirtualCardDuplicatesInnerCardholder}
   * @memberof AuthorizationVirtualCard
   */
  cardholder?: VirtualCardDuplicatesInnerCardholder;
  /**
   * Date when virtual card will expire
   * @type {string}
   * @memberof AuthorizationVirtualCard
   */
  expirationDate: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof AuthorizationVirtualCard
   */
  panMasked: string;
  /**
   * Bank Identification Number for virtual card. Generally will be the first four (4) to six (6) digits of the virtual card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof AuthorizationVirtualCard
   */
  readonly bin?: string | null;
  /**
   * Card status.
   * @type {string}
   * @memberof AuthorizationVirtualCard
   */
  status: AuthorizationVirtualCardStatusEnum | null;
}

/**
 * @export
 */
export const AuthorizationVirtualCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type AuthorizationVirtualCardStatusEnum = typeof AuthorizationVirtualCardStatusEnum[keyof typeof AuthorizationVirtualCardStatusEnum];

/**
 *
 * @export
 * @interface BulkExchange
 */
export interface BulkExchange {
  /**
   * List of non-Gateway Ids to be exchanged
   * @type {Array<string>}
   * @memberof BulkExchange
   */
  externalIds: Array<string>;
  /**
   * List of Gateway Account ids belonging to each externalId. ExternalIds not found in Gateway are omitted.
   * @type {Array<BulkExchangeResultsInner>}
   * @memberof BulkExchange
   */
  readonly results?: Array<BulkExchangeResultsInner>;
}
/**
 *
 * @export
 * @interface BulkExchangeResultsInner
 */
export interface BulkExchangeResultsInner {
  /**
   * non-Gateway entity id.
   * @type {string}
   * @memberof BulkExchangeResultsInner
   */
  readonly externalId?: string;
  /**
   * List of Gateway ids belonging to externalId
   * @type {Array<string>}
   * @memberof BulkExchangeResultsInner
   */
  readonly ids?: Array<string>;
}
/**
 *
 * @export
 * @interface BulkExchangeWithAccount
 */
export interface BulkExchangeWithAccount {
  /**
   * List of non-Gateway Ids to be exchanged
   * @type {Array<string>}
   * @memberof BulkExchangeWithAccount
   */
  externalIds: Array<string>;
  /**
   * List of Gateway Account ids belonging to each externalId. ExternalIds not found in Gateway are omitted.
   * @type {Array<BulkExchangeResultsInner>}
   * @memberof BulkExchangeWithAccount
   */
  readonly results?: Array<BulkExchangeResultsInner>;
  /**
   * Gateway ID of the account doing the look-up
   * @type {string}
   * @memberof BulkExchangeWithAccount
   */
  accountId?: string;
}
/**
 *
 * @export
 * @interface Card
 */
export interface Card {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Card
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof Card
   */
  externalId?: string | null;
  /**
   *
   * @type {CardCardholder}
   * @memberof Card
   */
  cardholder: CardCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof Card
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof Card
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof Card
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof Card
   */
  readonly bin?: string | null;
  /**
   *
   * @type {CardAccount}
   * @memberof Card
   */
  account: CardAccount;
  /**
   * Name printed on physical card.
   * @type {string}
   * @memberof Card
   */
  embossedName?: string | null;
  /**
   * Card network.
   * @type {string}
   * @memberof Card
   */
  network: CardNetworkEnum;
  /**
   * True if card is not printed to plastic.
   * @type {boolean}
   * @memberof Card
   */
  isGhost: boolean;
  /**
   * True if card does not exist with the issuer. Placeholder cards are created by the Gateway when transaction data is ingested that does not link to any existing issuer Card.
   * @type {boolean}
   * @memberof Card
   */
  isPlaceholder: boolean;
  /**
   * True if Card is a parent or control account card. Parent or control account cards are used to consolidate or oversee other cards.
   * @type {boolean}
   * @memberof Card
   */
  isParent: boolean;
  /**
   * Card status. May be unset.
   * @type {string}
   * @memberof Card
   */
  status?: CardStatusEnum;
  /**
   * True if card is able and configured to allow virtual card issuing.
   * @type {boolean}
   * @memberof Card
   */
  supportsVcnIssuing: boolean;
  /**
   *
   * @type {CardAllowance}
   * @memberof Card
   */
  allowance: CardAllowance | null;
  /**
   * Custom data field associated with the Card.
   * @type {string}
   * @memberof Card
   */
  customData: string;
  /**
   * Gateway cards in other connections for the customer with same externalId
   * @type {Array<CardDuplicatesInner>}
   * @memberof Card
   */
  readonly duplicates?: Array<CardDuplicatesInner>;
}

/**
 * @export
 */
export const CardNetworkEnum = {
  Unknown: 'unknown',
  Amex: 'amex',
  Mastercard: 'mastercard',
  Visa: 'visa',
  Demo: 'demo',
  Discover: 'discover',
  DinersClub: 'diners-club'
} as const;
export type CardNetworkEnum = typeof CardNetworkEnum[keyof typeof CardNetworkEnum];

/**
 * @export
 */
export const CardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type CardStatusEnum = typeof CardStatusEnum[keyof typeof CardStatusEnum];

/**
 * Parent account of the card
 * @export
 * @interface CardAccount
 */
export interface CardAccount {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CardAccount
   */
  id: string;
  /**
   * Issuer-provided unique id.
   * @type {string}
   * @memberof CardAccount
   */
  externalId: string;
  /**
   * User-friendly account name.
   * @type {string}
   * @memberof CardAccount
   */
  name: string;
  /**
   *
   * @type {CardAccountIssuer}
   * @memberof CardAccount
   */
  issuer: CardAccountIssuer | null;
  /**
   * Name of the institution.
   * @type {string}
   * @memberof CardAccount
   */
  institutionName: string;
}
/**
 * Issuer information.
 * @export
 * @interface CardAccountIssuer
 */
export interface CardAccountIssuer {
  /**
   * Issuer ID.
   * @type {string}
   * @memberof CardAccountIssuer
   */
  id: string;
  /**
   * User-friendly name.
   * @type {string}
   * @memberof CardAccountIssuer
   */
  name: string;
}
/**
 * The allowance associated with the card
 * @export
 * @interface CardAllowance
 */
export interface CardAllowance {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof CardAllowance
   */
  interval: CardAllowanceIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof CardAllowance
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof CardAllowance
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CardAllowance
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CardAllowance
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof CardAllowance
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof CardAllowance
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof CardAllowance
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof CardAllowance
   */
  allowedVendor?: string | null;
  /**
   * Available amount remaining on the allowance. A `null` value indicates `remainingBalance` not supported by Issuer.
   * @type {number}
   * @memberof CardAllowance
   */
  readonly remainingBalance?: number | null;
  /**
   * Next date and time when allowance transaction balance will be reset at. This field is based upon the `interval` field. For `continuous` allowance intervals this field will be empty.
   * @type {string}
   * @memberof CardAllowance
   */
  readonly balanceResetsAt?: string | null;
}

/**
 * @export
 */
export const CardAllowanceIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type CardAllowanceIntervalEnum = typeof CardAllowanceIntervalEnum[keyof typeof CardAllowanceIntervalEnum];

/**
 * Cardholder details.
 * @export
 * @interface CardCardholder
 */
export interface CardCardholder {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CardCardholder
   */
  id?: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CardCardholder
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof CardCardholder
   */
  firstName: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof CardCardholder
   */
  lastName: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof CardCardholder
   */
  email: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof CardCardholder
   */
  employeeId?: string;
}
/**
 *
 * @export
 * @interface CardDetail
 */
export interface CardDetail {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CardDetail
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CardDetail
   */
  externalId?: string | null;
  /**
   *
   * @type {CardCardholder}
   * @memberof CardDetail
   */
  cardholder: CardCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof CardDetail
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof CardDetail
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof CardDetail
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof CardDetail
   */
  readonly bin?: string | null;
  /**
   *
   * @type {CardAccount}
   * @memberof CardDetail
   */
  account: CardAccount;
  /**
   * Name printed on physical card.
   * @type {string}
   * @memberof CardDetail
   */
  embossedName?: string | null;
  /**
   * Card network.
   * @type {string}
   * @memberof CardDetail
   */
  network: CardDetailNetworkEnum;
  /**
   * True if card is not printed to plastic.
   * @type {boolean}
   * @memberof CardDetail
   */
  isGhost: boolean;
  /**
   * True if card does not exist with the issuer. Placeholder cards are created by the Gateway when transaction data is ingested that does not link to any existing issuer Card.
   * @type {boolean}
   * @memberof CardDetail
   */
  isPlaceholder: boolean;
  /**
   * True if Card is a parent or control account card. Parent or control account cards are used to consolidate or oversee other cards.
   * @type {boolean}
   * @memberof CardDetail
   */
  isParent: boolean;
  /**
   * Card status. May be unset.
   * @type {string}
   * @memberof CardDetail
   */
  status?: CardDetailStatusEnum;
  /**
   * True if card is able and configured to allow virtual card issuing.
   * @type {boolean}
   * @memberof CardDetail
   */
  supportsVcnIssuing: boolean;
  /**
   *
   * @type {CardAllowance}
   * @memberof CardDetail
   */
  allowance: CardAllowance | null;
  /**
   * Custom data field associated with the Card.
   * @type {string}
   * @memberof CardDetail
   */
  customData: string;
  /**
   * Gateway cards in other connections for the customer with same externalId
   * @type {Array<CardDuplicatesInner>}
   * @memberof CardDetail
   */
  readonly duplicates?: Array<CardDuplicatesInner>;
  /**
   *
   * @type {CardDetailBillingAddress}
   * @memberof CardDetail
   */
  billingAddress?: CardDetailBillingAddress;
  /**
   * All approvals assigned to the card
   * @type {Array<CardDetailApprovalsInner>}
   * @memberof CardDetail
   */
  readonly approvals?: Array<CardDetailApprovalsInner>;
  /**
   *
   * @type {CardDetailShipping}
   * @memberof CardDetail
   */
  shipping?: CardDetailShipping;
}

/**
 * @export
 */
export const CardDetailNetworkEnum = {
  Unknown: 'unknown',
  Amex: 'amex',
  Mastercard: 'mastercard',
  Visa: 'visa',
  Demo: 'demo',
  Discover: 'discover',
  DinersClub: 'diners-club'
} as const;
export type CardDetailNetworkEnum = typeof CardDetailNetworkEnum[keyof typeof CardDetailNetworkEnum];

/**
 * @export
 */
export const CardDetailStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type CardDetailStatusEnum = typeof CardDetailStatusEnum[keyof typeof CardDetailStatusEnum];

/**
 *
 * @export
 * @interface CardDetailApprovalsInner
 */
export interface CardDetailApprovalsInner {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof CardDetailApprovalsInner
   */
  interval: CardDetailApprovalsInnerIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof CardDetailApprovalsInner
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof CardDetailApprovalsInner
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CardDetailApprovalsInner
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CardDetailApprovalsInner
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof CardDetailApprovalsInner
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof CardDetailApprovalsInner
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof CardDetailApprovalsInner
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof CardDetailApprovalsInner
   */
  allowedVendor?: string | null;
  /**
   * Available amount remaining on the allowance. A `null` value indicates `remainingBalance` not supported by Issuer.
   * @type {number}
   * @memberof CardDetailApprovalsInner
   */
  readonly remainingBalance?: number | null;
  /**
   * Next date and time when allowance transaction balance will be reset at. This field is based upon the `interval` field. For `continuous` allowance intervals this field will be empty.
   * @type {string}
   * @memberof CardDetailApprovalsInner
   */
  readonly balanceResetsAt?: string | null;
  /**
   * Description for approved requisition. E.g. `October New York Trip`
   * @type {string}
   * @memberof CardDetailApprovalsInner
   */
  description: string;
  /**
   * Optional issuer-provided unique id.
   * @type {string}
   * @memberof CardDetailApprovalsInner
   */
  readonly externalId?: string;
}

/**
 * @export
 */
export const CardDetailApprovalsInnerIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type CardDetailApprovalsInnerIntervalEnum =
  typeof CardDetailApprovalsInnerIntervalEnum[keyof typeof CardDetailApprovalsInnerIntervalEnum];

/**
 * The billing address associated with the card
 * @export
 * @interface CardDetailBillingAddress
 */
export interface CardDetailBillingAddress {
  /**
   * Person or organization located at address.
   * @type {string}
   * @memberof CardDetailBillingAddress
   */
  attn?: string | null;
  /**
   * First line of address. E.g. 123 Fake Street
   * @type {string}
   * @memberof CardDetailBillingAddress
   */
  address1: string;
  /**
   * Second line of address. E.g. Apartment 312
   * @type {string}
   * @memberof CardDetailBillingAddress
   */
  address2?: string | null;
  /**
   * City of address
   * @type {string}
   * @memberof CardDetailBillingAddress
   */
  city: string;
  /**
   * US state or territory of address
   * @type {string}
   * @memberof CardDetailBillingAddress
   */
  state: string;
  /**
   * Country of address
   * @type {string}
   * @memberof CardDetailBillingAddress
   */
  country?: string;
  /**
   * Zipcode of address
   * @type {string}
   * @memberof CardDetailBillingAddress
   */
  zipCode: string;
}
/**
 * Shipping details if issuing a physical card.
 * @export
 * @interface CardDetailShipping
 */
export interface CardDetailShipping {
  /**
   *
   * @type {CardDetailShippingAddress}
   * @memberof CardDetailShipping
   */
  address: CardDetailShippingAddress;
  /**
   * Shipping service to be used. Is specific to the issuer.
   * @type {string}
   * @memberof CardDetailShipping
   */
  service?: CardDetailShippingServiceEnum;
  /**
   * Require delivery signature.
   * @type {boolean}
   * @memberof CardDetailShipping
   */
  requireSignature: boolean;
  /**
   * Phone number for recipient of shipment.
   * @type {string}
   * @memberof CardDetailShipping
   */
  phoneNumber?: string | null;
  /**
   * Shipping carrier.
   * @type {string}
   * @memberof CardDetailShipping
   */
  readonly carrier?: string;
  /**
   * Estimated time of arrival.
   * @type {string}
   * @memberof CardDetailShipping
   */
  readonly eta?: string;
  /**
   * Shipping status.
   * @type {string}
   * @memberof CardDetailShipping
   */
  readonly status?: CardDetailShippingStatusEnum;
  /**
   * Shipping tracking number.
   * @type {string}
   * @memberof CardDetailShipping
   */
  readonly trackingNumber?: string;
  /**
   * Shipping tracking URL.
   * @type {string}
   * @memberof CardDetailShipping
   */
  readonly trackingUrl?: string;
}

/**
 * @export
 */
export const CardDetailShippingServiceEnum = {
  Standard: 'Standard',
  Express: 'Express',
  Priority: 'Priority'
} as const;
export type CardDetailShippingServiceEnum = typeof CardDetailShippingServiceEnum[keyof typeof CardDetailShippingServiceEnum];

/**
 * @export
 */
export const CardDetailShippingStatusEnum = {
  Pending: 'Pending',
  Shipped: 'Shipped',
  Delivered: 'Delivered',
  Returned: 'Returned',
  Failed: 'Failed',
  Canceled: 'Canceled'
} as const;
export type CardDetailShippingStatusEnum = typeof CardDetailShippingStatusEnum[keyof typeof CardDetailShippingStatusEnum];

/**
 * Address for card to be shipped to.
 * @export
 * @interface CardDetailShippingAddress
 */
export interface CardDetailShippingAddress {
  /**
   * Person or organization located at address.
   * @type {string}
   * @memberof CardDetailShippingAddress
   */
  attn?: string | null;
  /**
   * First line of address. E.g. 123 Fake Street
   * @type {string}
   * @memberof CardDetailShippingAddress
   */
  address1: string;
  /**
   * Second line of address. E.g. Apartment 312
   * @type {string}
   * @memberof CardDetailShippingAddress
   */
  address2?: string | null;
  /**
   * City of address
   * @type {string}
   * @memberof CardDetailShippingAddress
   */
  city: string;
  /**
   * US state or territory of address
   * @type {string}
   * @memberof CardDetailShippingAddress
   */
  state: string;
  /**
   * Country of address
   * @type {string}
   * @memberof CardDetailShippingAddress
   */
  country?: string;
  /**
   * Zipcode of address
   * @type {string}
   * @memberof CardDetailShippingAddress
   */
  zipCode: string;
}
/**
 *
 * @export
 * @interface CardDuplicatesInner
 */
export interface CardDuplicatesInner {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CardDuplicatesInner
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CardDuplicatesInner
   */
  externalId?: string | null;
  /**
   *
   * @type {CardDuplicatesInnerCardholder}
   * @memberof CardDuplicatesInner
   */
  cardholder?: CardDuplicatesInnerCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof CardDuplicatesInner
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof CardDuplicatesInner
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof CardDuplicatesInner
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof CardDuplicatesInner
   */
  readonly bin?: string | null;
}
/**
 * Cardholder for Card.
 * @export
 * @interface CardDuplicatesInnerCardholder
 */
export interface CardDuplicatesInnerCardholder {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CardDuplicatesInnerCardholder
   */
  id?: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CardDuplicatesInnerCardholder
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof CardDuplicatesInnerCardholder
   */
  firstName: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof CardDuplicatesInnerCardholder
   */
  lastName: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof CardDuplicatesInnerCardholder
   */
  email: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof CardDuplicatesInnerCardholder
   */
  employeeId?: string;
}
/**
 *
 * @export
 * @interface CardPan
 */
export interface CardPan {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CardPan
   */
  id: string;
  /**
   * Card PAN.
   * @type {string}
   * @memberof CardPan
   */
  readonly pan?: string;
}
/**
 *
 * @export
 * @interface Cardholder
 */
export interface Cardholder {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Cardholder
   */
  id?: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof Cardholder
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof Cardholder
   */
  firstName: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof Cardholder
   */
  lastName: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof Cardholder
   */
  email: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof Cardholder
   */
  employeeId?: string;
  /**
   * Status representing current state of the onboarding process.
   * @type {string}
   * @memberof Cardholder
   */
  readonly status?: CardholderStatusEnum;
  /**
   * True/False if cardholder is required to accept Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof Cardholder
   */
  readonly tosAcceptanceRequired?: boolean;
  /**
   * True/False if cardholder may require an additional Know Your Company (KYC) review.
   * @type {boolean}
   * @memberof Cardholder
   */
  readonly kycReviewRequired?: boolean;
}

/**
 * @export
 */
export const CardholderStatusEnum = {
  Pending: 'pending',
  Active: 'active',
  Deactivated: 'deactivated'
} as const;
export type CardholderStatusEnum = typeof CardholderStatusEnum[keyof typeof CardholderStatusEnum];

/**
 *
 * @export
 * @interface CardholderDetail
 */
export interface CardholderDetail {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CardholderDetail
   */
  id?: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CardholderDetail
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof CardholderDetail
   */
  firstName: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof CardholderDetail
   */
  lastName: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof CardholderDetail
   */
  email: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof CardholderDetail
   */
  employeeId?: string;
  /**
   * Status representing current state of the onboarding process.
   * @type {string}
   * @memberof CardholderDetail
   */
  readonly status?: CardholderDetailStatusEnum;
  /**
   * True/False if cardholder is required to accept Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof CardholderDetail
   */
  readonly tosAcceptanceRequired?: boolean;
  /**
   * True/False if cardholder may require an additional Know Your Company (KYC) review.
   * @type {boolean}
   * @memberof CardholderDetail
   */
  readonly kycReviewRequired?: boolean;
  /**
   *
   * @type {CardholderDetailPersonalAddress}
   * @memberof CardholderDetail
   */
  personalAddress: CardholderDetailPersonalAddress;
  /**
   * Date of birth for cardholder.
   * @type {string}
   * @memberof CardholderDetail
   */
  dateOfBirth: string;
  /**
   * Primary phone number of cardholder.
   * @type {string}
   * @memberof CardholderDetail
   */
  primaryPhoneNumber: string;
  /**
   * Cardholder accepted Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof CardholderDetail
   */
  acceptedToS: boolean;
  /**
   * The datetime when cardholder accepted Terms of Service (ToS).
   * @type {string}
   * @memberof CardholderDetail
   */
  acceptedToSAt?: string;
  /**
   * IP address of logged in cardholder user.
   * @type {string}
   * @memberof CardholderDetail
   */
  loggedInIpAddress: string;
}

/**
 * @export
 */
export const CardholderDetailStatusEnum = {
  Pending: 'pending',
  Active: 'active',
  Deactivated: 'deactivated'
} as const;
export type CardholderDetailStatusEnum = typeof CardholderDetailStatusEnum[keyof typeof CardholderDetailStatusEnum];

/**
 * Personal address of cardholder.
 * @export
 * @interface CardholderDetailPersonalAddress
 */
export interface CardholderDetailPersonalAddress {
  /**
   * Person or organization located at address.
   * @type {string}
   * @memberof CardholderDetailPersonalAddress
   */
  attn?: string | null;
  /**
   * First line of address. E.g. 123 Fake Street
   * @type {string}
   * @memberof CardholderDetailPersonalAddress
   */
  address1: string;
  /**
   * Second line of address. E.g. Apartment 312
   * @type {string}
   * @memberof CardholderDetailPersonalAddress
   */
  address2?: string | null;
  /**
   * City of address
   * @type {string}
   * @memberof CardholderDetailPersonalAddress
   */
  city: string;
  /**
   * US state or territory of address
   * @type {string}
   * @memberof CardholderDetailPersonalAddress
   */
  state: string;
  /**
   * Country of address
   * @type {string}
   * @memberof CardholderDetailPersonalAddress
   */
  country?: string;
  /**
   * Zipcode of address
   * @type {string}
   * @memberof CardholderDetailPersonalAddress
   */
  zipCode: string;
}
/**
 *
 * @export
 * @interface Connection
 */
export interface Connection {
  /**
   * Unique ID. Example: conn-Wgx98Rbi8nQuL9ddn3mTk1
   * @type {string}
   * @memberof Connection
   */
  readonly id?: string;
  /**
   * Business unit the connection belongs to. Example: chromeriver
   * @type {string}
   * @memberof Connection
   */
  readonly businessUnit?: string;
  /**
   * Customer that the connection belongs to (business unit specific). Example: "1234567890"
   * @type {string}
   * @memberof Connection
   */
  customerId: string;
  /**
   *
   * @type {object}
   * @memberof Connection
   */
  metadata?: object | null;
  /**
   * Backend that powers the integration. Example: simulation
   * @type {string}
   * @memberof Connection
   */
  feedBackend: ConnectionFeedBackendEnum;
  /**
   * Current status. Example: active
   * @type {string}
   * @memberof Connection
   */
  status: ConnectionStatusEnum;
  /**
   * Accounts that belong to the connection
   * @type {Array<EmburseApiCompanyConnectionAccountsInner>}
   * @memberof Connection
   */
  accounts: Array<EmburseApiCompanyConnectionAccountsInner>;
}

/**
 * @export
 */
export const ConnectionFeedBackendEnum = {
  Simulation: 'simulation',
  Cdf3: 'cdf3',
  EmburseCards: 'emburse_cards',
  AmexFins: 'amex_fins',
  CapitalOne: 'capital_one',
  AmexFiles: 'amex_files',
  AmexCpg: 'amex_cpg',
  VisaFiles: 'visa_files',
  Finicity: 'finicity',
  AmexCel: 'amex_cel',
  EfsFiles: 'efs_files'
} as const;
export type ConnectionFeedBackendEnum = typeof ConnectionFeedBackendEnum[keyof typeof ConnectionFeedBackendEnum];

/**
 * @export
 */
export const ConnectionStatusEnum = {
  Active: 'active',
  Disconnected: 'disconnected',
  Pending: 'pending',
  Deactivated: 'deactivated'
} as const;
export type ConnectionStatusEnum = typeof ConnectionStatusEnum[keyof typeof ConnectionStatusEnum];

/**
 *
 * @export
 * @interface ConnectionDetail
 */
export interface ConnectionDetail {
  /**
   * Unique ID.
   * @type {string}
   * @memberof ConnectionDetail
   */
  readonly id?: string;
  /**
   * Business unit the connection belongs to.
   * @type {string}
   * @memberof ConnectionDetail
   */
  readonly businessUnit?: string;
  /**
   * Customer that the connection belongs to (business unit specific).
   * @type {string}
   * @memberof ConnectionDetail
   */
  customerId: string;
  /**
   *
   * @type {object}
   * @memberof ConnectionDetail
   */
  metadata?: object | null;
  /**
   * Backend that powers the integration.
   * @type {string}
   * @memberof ConnectionDetail
   */
  feedBackend: ConnectionDetailFeedBackendEnum;
  /**
   * Current status.
   * @type {string}
   * @memberof ConnectionDetail
   */
  status: ConnectionDetailStatusEnum;
  /**
   * Accounts that belong to the connection
   * @type {Array<ConnectionDetailAccountsInner>}
   * @memberof ConnectionDetail
   */
  accounts: Array<ConnectionDetailAccountsInner>;
  /**
   *
   * @type {ConnectionDetailConfiguration}
   * @memberof ConnectionDetail
   */
  _configuration: ConnectionDetailConfiguration;
}

/**
 * @export
 */
export const ConnectionDetailFeedBackendEnum = {
  Simulation: 'simulation',
  Cdf3: 'cdf3',
  EmburseCards: 'emburse_cards',
  AmexFins: 'amex_fins',
  CapitalOne: 'capital_one',
  AmexFiles: 'amex_files',
  AmexCpg: 'amex_cpg',
  VisaFiles: 'visa_files',
  Finicity: 'finicity',
  AmexCel: 'amex_cel',
  EfsFiles: 'efs_files'
} as const;
export type ConnectionDetailFeedBackendEnum = typeof ConnectionDetailFeedBackendEnum[keyof typeof ConnectionDetailFeedBackendEnum];

/**
 * @export
 */
export const ConnectionDetailStatusEnum = {
  Active: 'active',
  Disconnected: 'disconnected',
  Pending: 'pending',
  Deactivated: 'deactivated'
} as const;
export type ConnectionDetailStatusEnum = typeof ConnectionDetailStatusEnum[keyof typeof ConnectionDetailStatusEnum];

/**
 *
 * @export
 * @interface ConnectionDetailAccountsInner
 */
export interface ConnectionDetailAccountsInner {
  /**
   * Unique ID.
   * @type {string}
   * @memberof ConnectionDetailAccountsInner
   */
  id: string;
  /**
   * Issuer-provided unique id.
   * @type {string}
   * @memberof ConnectionDetailAccountsInner
   */
  externalId: string;
  /**
   * User-friendly account name.
   * @type {string}
   * @memberof ConnectionDetailAccountsInner
   */
  name: string;
  /**
   *
   * @type {CardAccountIssuer}
   * @memberof ConnectionDetailAccountsInner
   */
  issuer: CardAccountIssuer | null;
  /**
   * Name of the institution.
   * @type {string}
   * @memberof ConnectionDetailAccountsInner
   */
  institutionName: string;
}
/**
 *
 * @export
 * @interface ConnectionDetailConfiguration
 */
export interface ConnectionDetailConfiguration {
  /**
   *
   * @type {ConnectionDetailConfigurationOauth}
   * @memberof ConnectionDetailConfiguration
   */
  oauth?: ConnectionDetailConfigurationOauth;
  /**
   *
   * @type {ConnectionDetailConfigurationFilefeed}
   * @memberof ConnectionDetailConfiguration
   */
  filefeed?: ConnectionDetailConfigurationFilefeed;
}
/**
 *
 * @export
 * @interface ConnectionDetailConfigurationFilefeed
 */
export interface ConnectionDetailConfigurationFilefeed {
  /**
   *
   * @type {ConnectionDetailConfigurationFilefeedLastFile}
   * @memberof ConnectionDetailConfigurationFilefeed
   */
  lastFile?: ConnectionDetailConfigurationFilefeedLastFile;
  /**
   *
   * @type {number}
   * @memberof ConnectionDetailConfigurationFilefeed
   */
  readonly count?: number;
}
/**
 *
 * @export
 * @interface ConnectionDetailConfigurationFilefeedLastFile
 */
export interface ConnectionDetailConfigurationFilefeedLastFile {
  /**
   *
   * @type {string}
   * @memberof ConnectionDetailConfigurationFilefeedLastFile
   */
  readonly originalFileName?: string;
  /**
   *
   * @type {string}
   * @memberof ConnectionDetailConfigurationFilefeedLastFile
   */
  readonly uploadedAt?: string;
  /**
   *
   * @type {string}
   * @memberof ConnectionDetailConfigurationFilefeedLastFile
   */
  readonly processedAt?: string;
}
/**
 *
 * @export
 * @interface ConnectionDetailConfigurationOauth
 */
export interface ConnectionDetailConfigurationOauth {
  /**
   * Date and time of last successful data sync for Connection with this OAuth token.
   * @type {string}
   * @memberof ConnectionDetailConfigurationOauth
   */
  readonly lastSyncAt?: string;
  /**
   * Estimated number of days OAuth token will be able to be used before requiring user to re-consent. Null value indicates tokens will be indefinitely valid.
   * @type {string}
   * @memberof ConnectionDetailConfigurationOauth
   */
  readonly estimatedValidityDays?: string;
  /**
   * Date and time OAuth token was created.
   * @type {string}
   * @memberof ConnectionDetailConfigurationOauth
   */
  createdAt: string;
}
/**
 *
 * @export
 * @interface CreateAccountExchange
 */
export interface CreateAccountExchange {
  /**
   * Non-Gateway ID to be exchanged
   * @type {string}
   * @memberof CreateAccountExchange
   */
  externalId: string;
  /**
   * List of Gateway ids belonging to externalId
   * @type {Array<string>}
   * @memberof CreateAccountExchange
   */
  readonly ids?: Array<string>;
}
/**
 *
 * @export
 * @interface CreateAuthorizationExchange
 */
export interface CreateAuthorizationExchange {
  /**
   * Non-Gateway ID to be exchanged
   * @type {string}
   * @memberof CreateAuthorizationExchange
   */
  externalId: string;
  /**
   * Gateway ID of the account doing the look-up
   * @type {string}
   * @memberof CreateAuthorizationExchange
   */
  accountId?: string;
  /**
   * Gateway id.
   * @type {string}
   * @memberof CreateAuthorizationExchange
   */
  readonly id?: string;
}
/**
 *
 * @export
 * @interface CreateCard
 */
export interface CreateCard {
  /**
   *
   * @type {CreateCardAllowance}
   * @memberof CreateCard
   */
  allowance: CreateCardAllowance;
  /**
   * True if card is not printed to plastic.
   * @type {boolean}
   * @memberof CreateCard
   */
  isGhost: boolean;
  /**
   * DEPRECATED (Will be removed). Required if card holder not set. Name printed on physical card.
   * @type {string}
   * @memberof CreateCard
   */
  embossedName?: string | null;
  /**
   * Optional description for the purpose of the card.
   * @type {string}
   * @memberof CreateCard
   */
  description?: string | null;
  /**
   *
   * @type {CreateCardShippingAddress}
   * @memberof CreateCard
   */
  shippingAddress?: CreateCardShippingAddress;
  /**
   *
   * @type {CreateCardCardholder}
   * @memberof CreateCard
   */
  cardholder?: CreateCardCardholder;
  /**
   *
   * @type {CreateCardShipping}
   * @memberof CreateCard
   */
  shipping?: CreateCardShipping;
}
/**
 * Allowance for card.
 * @export
 * @interface CreateCardAllowance
 */
export interface CreateCardAllowance {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof CreateCardAllowance
   */
  interval: CreateCardAllowanceIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof CreateCardAllowance
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof CreateCardAllowance
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CreateCardAllowance
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CreateCardAllowance
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof CreateCardAllowance
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof CreateCardAllowance
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof CreateCardAllowance
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof CreateCardAllowance
   */
  allowedVendor?: string | null;
  /**
   * Available amount remaining on the allowance. A `null` value indicates `remainingBalance` not supported by Issuer.
   * @type {number}
   * @memberof CreateCardAllowance
   */
  readonly remainingBalance?: number | null;
  /**
   * Next date and time when allowance transaction balance will be reset at. This field is based upon the `interval` field. For `continuous` allowance intervals this field will be empty.
   * @type {string}
   * @memberof CreateCardAllowance
   */
  readonly balanceResetsAt?: string | null;
}

/**
 * @export
 */
export const CreateCardAllowanceIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type CreateCardAllowanceIntervalEnum = typeof CreateCardAllowanceIntervalEnum[keyof typeof CreateCardAllowanceIntervalEnum];

/**
 * Cardholder information
 * @export
 * @interface CreateCardCardholder
 */
export interface CreateCardCardholder {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  id?: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  firstName: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  lastName: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  email: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  employeeId?: string;
  /**
   * Status representing current state of the onboarding process.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  readonly status?: CreateCardCardholderStatusEnum;
  /**
   * True/False if cardholder is required to accept Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof CreateCardCardholder
   */
  readonly tosAcceptanceRequired?: boolean;
  /**
   * True/False if cardholder may require an additional Know Your Company (KYC) review.
   * @type {boolean}
   * @memberof CreateCardCardholder
   */
  readonly kycReviewRequired?: boolean;
  /**
   *
   * @type {CardholderDetailPersonalAddress}
   * @memberof CreateCardCardholder
   */
  personalAddress: CardholderDetailPersonalAddress;
  /**
   * Date of birth for cardholder.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  dateOfBirth: string;
  /**
   * Primary phone number of cardholder.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  primaryPhoneNumber: string;
  /**
   * Cardholder accepted Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof CreateCardCardholder
   */
  acceptedToS: boolean;
  /**
   * The datetime when cardholder accepted Terms of Service (ToS).
   * @type {string}
   * @memberof CreateCardCardholder
   */
  acceptedToSAt?: string;
  /**
   * IP address of logged in cardholder user.
   * @type {string}
   * @memberof CreateCardCardholder
   */
  loggedInIpAddress: string;
}

/**
 * @export
 */
export const CreateCardCardholderStatusEnum = {
  Pending: 'pending',
  Active: 'active',
  Deactivated: 'deactivated'
} as const;
export type CreateCardCardholderStatusEnum = typeof CreateCardCardholderStatusEnum[keyof typeof CreateCardCardholderStatusEnum];

/**
 *
 * @export
 * @interface CreateCardExchange
 */
export interface CreateCardExchange {
  /**
   * Non-Gateway ID to be exchanged
   * @type {string}
   * @memberof CreateCardExchange
   */
  externalId: string;
  /**
   * Gateway ID of the account doing the look-up
   * @type {string}
   * @memberof CreateCardExchange
   */
  accountId?: string;
  /**
   * Gateway id.
   * @type {string}
   * @memberof CreateCardExchange
   */
  readonly id?: string;
}
/**
 * Shipping details if issuing a physical card. This overrides deprecated `shippingAddress` field.
 * @export
 * @interface CreateCardShipping
 */
export interface CreateCardShipping {
  /**
   *
   * @type {CardDetailShippingAddress}
   * @memberof CreateCardShipping
   */
  address: CardDetailShippingAddress;
  /**
   * Shipping service to be used. Is specific to the issuer.
   * @type {string}
   * @memberof CreateCardShipping
   */
  service?: CreateCardShippingServiceEnum;
  /**
   * Require delivery signature.
   * @type {boolean}
   * @memberof CreateCardShipping
   */
  requireSignature: boolean;
  /**
   * Phone number for recipient of shipment.
   * @type {string}
   * @memberof CreateCardShipping
   */
  phoneNumber?: string | null;
  /**
   * Shipping carrier.
   * @type {string}
   * @memberof CreateCardShipping
   */
  readonly carrier?: string;
  /**
   * Estimated time of arrival.
   * @type {string}
   * @memberof CreateCardShipping
   */
  readonly eta?: string;
  /**
   * Shipping status.
   * @type {string}
   * @memberof CreateCardShipping
   */
  readonly status?: CreateCardShippingStatusEnum;
  /**
   * Shipping tracking number.
   * @type {string}
   * @memberof CreateCardShipping
   */
  readonly trackingNumber?: string;
  /**
   * Shipping tracking URL.
   * @type {string}
   * @memberof CreateCardShipping
   */
  readonly trackingUrl?: string;
}

/**
 * @export
 */
export const CreateCardShippingServiceEnum = {
  Standard: 'Standard',
  Express: 'Express',
  Priority: 'Priority'
} as const;
export type CreateCardShippingServiceEnum = typeof CreateCardShippingServiceEnum[keyof typeof CreateCardShippingServiceEnum];

/**
 * @export
 */
export const CreateCardShippingStatusEnum = {
  Pending: 'Pending',
  Shipped: 'Shipped',
  Delivered: 'Delivered',
  Returned: 'Returned',
  Failed: 'Failed',
  Canceled: 'Canceled'
} as const;
export type CreateCardShippingStatusEnum = typeof CreateCardShippingStatusEnum[keyof typeof CreateCardShippingStatusEnum];

/**
 * Deprecated and replaced by `shipping` field. Required if issuing a physical card. Address for card to be shipped to.
 * @export
 * @interface CreateCardShippingAddress
 */
export interface CreateCardShippingAddress {
  /**
   * Person or organization located at address.
   * @type {string}
   * @memberof CreateCardShippingAddress
   */
  attn?: string | null;
  /**
   * First line of address. E.g. 123 Fake Street
   * @type {string}
   * @memberof CreateCardShippingAddress
   */
  address1: string;
  /**
   * Second line of address. E.g. Apartment 312
   * @type {string}
   * @memberof CreateCardShippingAddress
   */
  address2?: string | null;
  /**
   * City of address
   * @type {string}
   * @memberof CreateCardShippingAddress
   */
  city: string;
  /**
   * US state or territory of address
   * @type {string}
   * @memberof CreateCardShippingAddress
   */
  state: string;
  /**
   * Country of address
   * @type {string}
   * @memberof CreateCardShippingAddress
   */
  country?: string;
  /**
   * Zipcode of address
   * @type {string}
   * @memberof CreateCardShippingAddress
   */
  zipCode: string;
}
/**
 *
 * @export
 * @interface CreateConnection
 */
export interface CreateConnection {
  /**
   * IssuerId for the Connection
   * @type {string}
   * @memberof CreateConnection
   */
  connectTo: string;
  /**
   *
   * @type {CreateConnectionConfig}
   * @memberof CreateConnection
   */
  config?: CreateConnectionConfig;
  /**
   * Optional business-unit specific metadata for Connection
   * @type {object}
   * @memberof CreateConnection
   */
  metadata?: object;
}
/**
 * Optional configuration for Issuer.
 * @export
 * @interface CreateConnectionConfig
 */
export interface CreateConnectionConfig {
  /**
   *
   * @type {CreateConnectionConfigAmexFdxConfig}
   * @memberof CreateConnectionConfig
   */
  amexFdxConfig?: CreateConnectionConfigAmexFdxConfig;
  /**
   *
   * @type {CreateConnectionConfigAmexGLMigrationConfig}
   * @memberof CreateConnectionConfig
   */
  amexGLMigrationConfig?: CreateConnectionConfigAmexGLMigrationConfig;
  /**
   *
   * @type {CreateConnectionConfigAmexIssuingConfig}
   * @memberof CreateConnectionConfig
   */
  amexIssuingConfig?: CreateConnectionConfigAmexIssuingConfig;
  /**
   *
   * @type {CreateConnectionConfigEmburseCardsMigrationConfig}
   * @memberof CreateConnectionConfig
   */
  emburseCardsMigrationConfig?: CreateConnectionConfigEmburseCardsMigrationConfig;
  /**
   *
   * @type {CreateConnectionConfigFinicityMigrationConfig}
   * @memberof CreateConnectionConfig
   */
  finicityMigrationConfig?: CreateConnectionConfigFinicityMigrationConfig;
  /**
   *
   * @type {CreateConnectionConfigCdf3MigrationConfig}
   * @memberof CreateConnectionConfig
   */
  cdf3MigrationConfig?: CreateConnectionConfigCdf3MigrationConfig;
}
/**
 * Configuration details for issuing cards via Amex CPG.
 * @export
 * @interface CreateConnectionConfigAmexFdxConfig
 */
export interface CreateConnectionConfigAmexFdxConfig {
  /**
   * Optional flag to only pull and fetch data from parent card.
   * @type {boolean}
   * @memberof CreateConnectionConfigAmexFdxConfig
   */
  parentOnly?: boolean;
}
/**
 * Configuration details for migrating an existing Amex GL File program.
 * @export
 * @interface CreateConnectionConfigAmexGLMigrationConfig
 */
export interface CreateConnectionConfigAmexGLMigrationConfig {
  /**
   * AMEX Basic Control Account Number to use for this connection, only transactions for this basic control account will be imported
   * @type {string}
   * @memberof CreateConnectionConfigAmexGLMigrationConfig
   */
  basicControlAccount: string;
}
/**
 * Configuration details for issuing cards via Amex CPG.
 * @export
 * @interface CreateConnectionConfigAmexIssuingConfig
 */
export interface CreateConnectionConfigAmexIssuingConfig {
  /**
   * Registered name of the Company or Organization in Amex.
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfig
   */
  organizationName: string;
  /**
   *
   * @type {CreateConnectionConfigAmexIssuingConfigBillingAddress}
   * @memberof CreateConnectionConfigAmexIssuingConfig
   */
  billingAddress: CreateConnectionConfigAmexIssuingConfigBillingAddress;
  /**
   * First name of requesting user.
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfig
   */
  requestorFirstName: string;
  /**
   * Last name of requesting user.
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfig
   */
  requestorLastName: string;
  /**
   * Email of requesting user.
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfig
   */
  requestorEmail: string;
  /**
   * Phone number of requesting user. US only. No extensions allowed.
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfig
   */
  phoneNumber?: string;
}
/**
 * Default billing address for organization.
 * @export
 * @interface CreateConnectionConfigAmexIssuingConfigBillingAddress
 */
export interface CreateConnectionConfigAmexIssuingConfigBillingAddress {
  /**
   * Person or organization located at address.
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfigBillingAddress
   */
  attn?: string | null;
  /**
   * First line of address. E.g. 123 Fake Street
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfigBillingAddress
   */
  address1: string;
  /**
   * Second line of address. E.g. Apartment 312
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfigBillingAddress
   */
  address2?: string | null;
  /**
   * City of address
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfigBillingAddress
   */
  city: string;
  /**
   * US state or territory of address
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfigBillingAddress
   */
  state: string;
  /**
   * Country of address
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfigBillingAddress
   */
  country?: string;
  /**
   * Zipcode of address
   * @type {string}
   * @memberof CreateConnectionConfigAmexIssuingConfigBillingAddress
   */
  zipCode: string;
}
/**
 * Configuration details for migrating an existing CDF3 file feed.
 * @export
 * @interface CreateConnectionConfigCdf3MigrationConfig
 */
export interface CreateConnectionConfigCdf3MigrationConfig {
  /**
   * Corporation number (from CDF3 file) associated with company.
   * @type {string}
   * @memberof CreateConnectionConfigCdf3MigrationConfig
   */
  corporationNumber: string;
  /**
   * Issuer number (from CDF3 file) associated with company.
   * @type {string}
   * @memberof CreateConnectionConfigCdf3MigrationConfig
   */
  issuerNumber: string;
}
/**
 * Configuration details for migrating an existing Emburse Cards program.
 * @export
 * @interface CreateConnectionConfigEmburseCardsMigrationConfig
 */
export interface CreateConnectionConfigEmburseCardsMigrationConfig {
  /**
   * Emburse Cards program access token
   * @type {string}
   * @memberof CreateConnectionConfigEmburseCardsMigrationConfig
   */
  accessToken: string;
  /**
   * Optional number of last statement line to import after.
   * @type {number}
   * @memberof CreateConnectionConfigEmburseCardsMigrationConfig
   */
  lastStatementLineNumber?: number;
  /**
   * Optional flag to only pull and fetch data from after the last Emburse Cards StatementLine.number. Will override `lastStatementLineNumber` value.
   * @type {boolean}
   * @memberof CreateConnectionConfigEmburseCardsMigrationConfig
   */
  syncFromLatestStatementLine?: boolean;
}
/**
 * Configuration details for migrating an existing Finicity customer.
 * @export
 * @interface CreateConnectionConfigFinicityMigrationConfig
 */
export interface CreateConnectionConfigFinicityMigrationConfig {
  /**
   * Finicity partner Id associated with customer.
   * @type {string}
   * @memberof CreateConnectionConfigFinicityMigrationConfig
   */
  partnerId: string;
  /**
   * Finicity customer Id to be migrated.
   * @type {string}
   * @memberof CreateConnectionConfigFinicityMigrationConfig
   */
  customerId: string;
}
/**
 *
 * @export
 * @interface CreateSettlementExchange
 */
export interface CreateSettlementExchange {
  /**
   * Non-Gateway ID to be exchanged
   * @type {string}
   * @memberof CreateSettlementExchange
   */
  externalId: string;
  /**
   * Gateway ID of the account doing the look-up
   * @type {string}
   * @memberof CreateSettlementExchange
   */
  accountId?: string;
  /**
   * Gateway id.
   * @type {string}
   * @memberof CreateSettlementExchange
   */
  readonly id?: string;
}
/**
 *
 * @export
 * @interface CreateSimBankAuthorization
 */
export interface CreateSimBankAuthorization {
  /**
   * Merchant name.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantName?: string;
  /**
   * Merchant address line.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantAddress?: string;
  /**
   * Merchant city.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantCity?: string;
  /**
   * Merchant state.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantState?: string;
  /**
   * Merchant zip code.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantZipCode?: string;
  /**
   * Merchant country.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantCountry?: string;
  /**
   * Merchant mid.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantMid?: string;
  /**
   * Merchant Category Code (Four-digit ISO 18245).
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  merchantMcc?: string;
  /**
   * Requested authorization amount. Negative for debits and positive for credits.
   * @type {number}
   * @memberof CreateSimBankAuthorization
   */
  requestedAmount: number;
  /**
   * Authorized amount. Negative for debits and positive for credits.
   * @type {number}
   * @memberof CreateSimBankAuthorization
   */
  authorizedAmount?: number;
  /**
   * Time authorization was charged.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  chargedAt?: string;
  /**
   * Authorization memo.
   * @type {string}
   * @memberof CreateSimBankAuthorization
   */
  memo?: string;
}
/**
 *
 * @export
 * @interface CreateSimBankSettlement
 */
export interface CreateSimBankSettlement {
  /**
   * Amount to post. Negative for debits and positive for credits.
   * @type {number}
   * @memberof CreateSimBankSettlement
   */
  amount: number;
  /**
   * Time settlement was posted.
   * @type {string}
   * @memberof CreateSimBankSettlement
   */
  postedAt?: string;
  /**
   * Close authorization once posted.
   * @type {boolean}
   * @memberof CreateSimBankSettlement
   */
  closeAuth?: boolean;
}
/**
 *
 * @export
 * @interface CreateVirtualCard
 */
export interface CreateVirtualCard {
  /**
   *
   * @type {CreateVirtualCardCard}
   * @memberof CreateVirtualCard
   */
  card: CreateVirtualCardCard;
  /**
   *
   * @type {CreateVirtualCardCardholder}
   * @memberof CreateVirtualCard
   */
  cardholder?: CreateVirtualCardCardholder;
  /**
   * Description for virtual card
   * @type {string}
   * @memberof CreateVirtualCard
   */
  description?: string;
  /**
   *
   * @type {CreateVirtualCardAllowance}
   * @memberof CreateVirtualCard
   */
  allowance: CreateVirtualCardAllowance;
  /**
   *
   * @type {CreateVirtualCardCardRequestor}
   * @memberof CreateVirtualCard
   */
  cardRequestor?: CreateVirtualCardCardRequestor;
}
/**
 * Allowance for virtual card
 * @export
 * @interface CreateVirtualCardAllowance
 */
export interface CreateVirtualCardAllowance {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof CreateVirtualCardAllowance
   */
  interval: CreateVirtualCardAllowanceIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof CreateVirtualCardAllowance
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof CreateVirtualCardAllowance
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CreateVirtualCardAllowance
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof CreateVirtualCardAllowance
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof CreateVirtualCardAllowance
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof CreateVirtualCardAllowance
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof CreateVirtualCardAllowance
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof CreateVirtualCardAllowance
   */
  allowedVendor?: string | null;
  /**
   * Available amount remaining on the allowance. A `null` value indicates `remainingBalance` not supported by Issuer.
   * @type {number}
   * @memberof CreateVirtualCardAllowance
   */
  readonly remainingBalance?: number | null;
  /**
   * Next date and time when allowance transaction balance will be reset at. This field is based upon the `interval` field. For `continuous` allowance intervals this field will be empty.
   * @type {string}
   * @memberof CreateVirtualCardAllowance
   */
  readonly balanceResetsAt?: string | null;
}

/**
 * @export
 */
export const CreateVirtualCardAllowanceIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type CreateVirtualCardAllowanceIntervalEnum =
  typeof CreateVirtualCardAllowanceIntervalEnum[keyof typeof CreateVirtualCardAllowanceIntervalEnum];

/**
 * Id of parent Card virtual card will stem from
 * @export
 * @interface CreateVirtualCardCard
 */
export interface CreateVirtualCardCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CreateVirtualCardCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CreateVirtualCardCard
   */
  externalId?: string | null;
  /**
   *
   * @type {CardDuplicatesInnerCardholder}
   * @memberof CreateVirtualCardCard
   */
  cardholder?: CardDuplicatesInnerCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof CreateVirtualCardCard
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof CreateVirtualCardCard
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof CreateVirtualCardCard
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof CreateVirtualCardCard
   */
  readonly bin?: string | null;
}
/**
 * Details of user requesting the virtual card, used for fraud mitigation
 * @export
 * @interface CreateVirtualCardCardRequestor
 */
export interface CreateVirtualCardCardRequestor {
  /**
   * Cardholder ID of the requesting user.
   * @type {string}
   * @memberof CreateVirtualCardCardRequestor
   */
  cardholderId: string;
  /**
   * Device ID of user requesting the card.
   * @type {string}
   * @memberof CreateVirtualCardCardRequestor
   */
  deviceId: string;
  /**
   * IP address of user requesting the card.
   * @type {string}
   * @memberof CreateVirtualCardCardRequestor
   */
  loggedInIpAddress: string;
  /**
   * Session ID of the user requesting the card.
   * @type {string}
   * @memberof CreateVirtualCardCardRequestor
   */
  sessionId: string;
}
/**
 * Cardholder for virtual card to be assigned to
 * @export
 * @interface CreateVirtualCardCardholder
 */
export interface CreateVirtualCardCardholder {
  /**
   * Unique ID.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  id?: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  firstName: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  lastName: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  email: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  employeeId?: string;
  /**
   * Status representing current state of the onboarding process.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  readonly status?: CreateVirtualCardCardholderStatusEnum;
  /**
   * True/False if cardholder is required to accept Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof CreateVirtualCardCardholder
   */
  readonly tosAcceptanceRequired?: boolean;
  /**
   * True/False if cardholder may require an additional Know Your Company (KYC) review.
   * @type {boolean}
   * @memberof CreateVirtualCardCardholder
   */
  readonly kycReviewRequired?: boolean;
  /**
   *
   * @type {CardholderDetailPersonalAddress}
   * @memberof CreateVirtualCardCardholder
   */
  personalAddress: CardholderDetailPersonalAddress;
  /**
   * Date of birth for cardholder.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  dateOfBirth: string;
  /**
   * Primary phone number of cardholder.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  primaryPhoneNumber: string;
  /**
   * Cardholder accepted Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof CreateVirtualCardCardholder
   */
  acceptedToS: boolean;
  /**
   * The datetime when cardholder accepted Terms of Service (ToS).
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  acceptedToSAt?: string;
  /**
   * IP address of logged in cardholder user.
   * @type {string}
   * @memberof CreateVirtualCardCardholder
   */
  loggedInIpAddress: string;
}

/**
 * @export
 */
export const CreateVirtualCardCardholderStatusEnum = {
  Pending: 'pending',
  Active: 'active',
  Deactivated: 'deactivated'
} as const;
export type CreateVirtualCardCardholderStatusEnum =
  typeof CreateVirtualCardCardholderStatusEnum[keyof typeof CreateVirtualCardCardholderStatusEnum];

/**
 *
 * @export
 * @interface EmbeddedCardImage
 */
export interface EmbeddedCardImage {
  /**
   * Format for generated card image, default value is `standard`. `standard` contains all details a physically printed card will have (including embossed name and expiration). `minimal` only contains masked PAN and card description. `json` simply returns a JSON object containing PAN and CVV.
   * @type {string}
   * @memberof EmbeddedCardImage
   */
  cardFormat?: EmbeddedCardImageCardFormatEnum;
  /**
   * URL for image
   * @type {string}
   * @memberof EmbeddedCardImage
   */
  readonly src?: string;
  /**
   *
   * @type {string}
   * @memberof EmbeddedCardImage
   */
  readonly expiresAt?: string;
}

/**
 * @export
 */
export const EmbeddedCardImageCardFormatEnum = {
  Standard: 'standard',
  Minimal: 'minimal',
  Json: 'json'
} as const;
export type EmbeddedCardImageCardFormatEnum = typeof EmbeddedCardImageCardFormatEnum[keyof typeof EmbeddedCardImageCardFormatEnum];

/**
 *
 * @export
 * @interface EmburseApiCompany
 */
export interface EmburseApiCompany {
  /**
   *
   * @type {EmburseApiCompanyConnection}
   * @memberof EmburseApiCompany
   */
  connection: EmburseApiCompanyConnection;
  /**
   *
   * @type {EmburseApiCompanyCompany}
   * @memberof EmburseApiCompany
   */
  company: EmburseApiCompanyCompany;
}
/**
 *
 * @export
 * @interface EmburseApiCompanyCompany
 */
export interface EmburseApiCompanyCompany {
  /**
   *
   * @type {EmburseApiCompanyCompanyBillingAddress}
   * @memberof EmburseApiCompanyCompany
   */
  billing_address: EmburseApiCompanyCompanyBillingAddress;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompany
   */
  created_at: string;
  /**
   *
   * @type {EmburseApiCompanyCompanyPartner}
   * @memberof EmburseApiCompanyCompany
   */
  partner: EmburseApiCompanyCompanyPartner;
  /**
   *
   * @type {EmburseApiCompanyCompanyShippingAddress}
   * @memberof EmburseApiCompanyCompany
   */
  shipping_address: EmburseApiCompanyCompanyShippingAddress;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompany
   */
  id: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompany
   */
  name: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompany
   */
  website: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompany
   */
  status: EmburseApiCompanyCompanyStatusEnum;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompany
   */
  verification_status: EmburseApiCompanyCompanyVerificationStatusEnum;
}

/**
 * @export
 */
export const EmburseApiCompanyCompanyStatusEnum = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected'
} as const;
export type EmburseApiCompanyCompanyStatusEnum = typeof EmburseApiCompanyCompanyStatusEnum[keyof typeof EmburseApiCompanyCompanyStatusEnum];

/**
 * @export
 */
export const EmburseApiCompanyCompanyVerificationStatusEnum = {
  Unsubmitted: 'unsubmitted',
  Pending: 'pending',
  Verified: 'verified',
  Unverified: 'unverified',
  Rejected: 'rejected'
} as const;
export type EmburseApiCompanyCompanyVerificationStatusEnum =
  typeof EmburseApiCompanyCompanyVerificationStatusEnum[keyof typeof EmburseApiCompanyCompanyVerificationStatusEnum];

/**
 *
 * @export
 * @interface EmburseApiCompanyCompanyBillingAddress
 */
export interface EmburseApiCompanyCompanyBillingAddress {
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyBillingAddress
   */
  address_1: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyBillingAddress
   */
  address_2: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyBillingAddress
   */
  city: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyBillingAddress
   */
  state: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyBillingAddress
   */
  postal_code: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyBillingAddress
   */
  zip_code: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyBillingAddress
   */
  country: string;
}
/**
 *
 * @export
 * @interface EmburseApiCompanyCompanyPartner
 */
export interface EmburseApiCompanyCompanyPartner {
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyPartner
   */
  id: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyPartner
   */
  token: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyPartner
   */
  brand_name: string;
}
/**
 *
 * @export
 * @interface EmburseApiCompanyCompanyShippingAddress
 */
export interface EmburseApiCompanyCompanyShippingAddress {
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyShippingAddress
   */
  id: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyShippingAddress
   */
  url: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyShippingAddress
   */
  address_1: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyShippingAddress
   */
  address_2: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyShippingAddress
   */
  city: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyShippingAddress
   */
  state: string;
  /**
   *
   * @type {string}
   * @memberof EmburseApiCompanyCompanyShippingAddress
   */
  zip_code: string;
}
/**
 *
 * @export
 * @interface EmburseApiCompanyConnection
 */
export interface EmburseApiCompanyConnection {
  /**
   * Unique ID. Example: conn-Wgx98Rbi8nQuL9ddn3mTk1
   * @type {string}
   * @memberof EmburseApiCompanyConnection
   */
  readonly id?: string;
  /**
   * Business unit the connection belongs to. Example: chromeriver
   * @type {string}
   * @memberof EmburseApiCompanyConnection
   */
  readonly businessUnit?: string;
  /**
   * Customer that the connection belongs to (business unit specific). Example: "1234567890"
   * @type {string}
   * @memberof EmburseApiCompanyConnection
   */
  customerId: string;
  /**
   *
   * @type {object}
   * @memberof EmburseApiCompanyConnection
   */
  metadata?: object | null;
  /**
   * Backend that powers the integration. Example: simulation
   * @type {string}
   * @memberof EmburseApiCompanyConnection
   */
  feedBackend: EmburseApiCompanyConnectionFeedBackendEnum;
  /**
   * Current status. Example: active
   * @type {string}
   * @memberof EmburseApiCompanyConnection
   */
  status: EmburseApiCompanyConnectionStatusEnum;
  /**
   * Accounts that belong to the connection
   * @type {Array<EmburseApiCompanyConnectionAccountsInner>}
   * @memberof EmburseApiCompanyConnection
   */
  accounts: Array<EmburseApiCompanyConnectionAccountsInner>;
}

/**
 * @export
 */
export const EmburseApiCompanyConnectionFeedBackendEnum = {
  Simulation: 'simulation',
  Cdf3: 'cdf3',
  EmburseCards: 'emburse_cards',
  AmexFins: 'amex_fins',
  CapitalOne: 'capital_one',
  AmexFiles: 'amex_files',
  AmexCpg: 'amex_cpg',
  VisaFiles: 'visa_files',
  Finicity: 'finicity',
  AmexCel: 'amex_cel',
  EfsFiles: 'efs_files'
} as const;
export type EmburseApiCompanyConnectionFeedBackendEnum =
  typeof EmburseApiCompanyConnectionFeedBackendEnum[keyof typeof EmburseApiCompanyConnectionFeedBackendEnum];

/**
 * @export
 */
export const EmburseApiCompanyConnectionStatusEnum = {
  Active: 'active',
  Disconnected: 'disconnected',
  Pending: 'pending',
  Deactivated: 'deactivated'
} as const;
export type EmburseApiCompanyConnectionStatusEnum =
  typeof EmburseApiCompanyConnectionStatusEnum[keyof typeof EmburseApiCompanyConnectionStatusEnum];

/**
 *
 * @export
 * @interface EmburseApiCompanyConnectionAccountsInner
 */
export interface EmburseApiCompanyConnectionAccountsInner {
  /**
   * Unique ID. Example: acct-8KXmFY9SQtjKwqdiiMmrgg
   * @type {string}
   * @memberof EmburseApiCompanyConnectionAccountsInner
   */
  id: string;
  /**
   * Issuer-provided unique id. Example: cae1a858-7592-4298-96af-2681f9e4f1b3
   * @type {string}
   * @memberof EmburseApiCompanyConnectionAccountsInner
   */
  externalId: string;
  /**
   * User-friendly account name. Example: ACME INC #8696
   * @type {string}
   * @memberof EmburseApiCompanyConnectionAccountsInner
   */
  name: string;
  /**
   *
   * @type {EmburseApiCompanyConnectionAccountsInnerIssuer}
   * @memberof EmburseApiCompanyConnectionAccountsInner
   */
  issuer: EmburseApiCompanyConnectionAccountsInnerIssuer | null;
  /**
   * Name of the institution. Example: Silicon Valley Bank
   * @type {string}
   * @memberof EmburseApiCompanyConnectionAccountsInner
   */
  institutionName: string;
}
/**
 * Issuer information.
 * @export
 * @interface EmburseApiCompanyConnectionAccountsInnerIssuer
 */
export interface EmburseApiCompanyConnectionAccountsInnerIssuer {
  /**
   * Issuer ID. Example: svb
   * @type {string}
   * @memberof EmburseApiCompanyConnectionAccountsInnerIssuer
   */
  id: string;
  /**
   * User-friendly name. Example: Silicon Valley Bank
   * @type {string}
   * @memberof EmburseApiCompanyConnectionAccountsInnerIssuer
   */
  name: string;
}
/**
 *
 * @export
 * @interface Event
 */
export interface Event {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Event
   */
  id: string;
  /**
   * Event time.
   * @type {string}
   * @memberof Event
   */
  createdAt: string;
  /**
   * Event data. When `eventType` is "authorization.created", the data is an authorization object.When `eventType` is "settlements.synced", this field is `null`.
   * @type {object}
   * @memberof Event
   */
  data: object | null;
  /**
   * Type of event.
   * @type {string}
   * @memberof Event
   */
  eventType: EventEventTypeEnum;
  /**
   * ID of the resource the Event corresponds to.
   * @type {string}
   * @memberof Event
   */
  resourceId: string;
}

/**
 * @export
 */
export const EventEventTypeEnum = {
  CardCreated: 'card.created',
  CardUpdated: 'card.updated',
  CardShippingUpdated: 'card.shipping_updated',
  VirtualCardCreated: 'virtualCard.created',
  VirtualCardUpdated: 'virtualCard.updated',
  AuthorizationCreated: 'authorization.created',
  AuthorizationExpired: 'authorization.expired',
  SettlementUpdated: 'settlement.updated',
  SettlementCorrected: 'settlement.corrected',
  SettlementsSynced: 'settlements.synced',
  StatementCreated: 'statement.created',
  ConnectionActive: 'connection.active',
  ConnectionCreated: 'connection.created',
  ConnectionDisconnected: 'connection.disconnected',
  ConnectionDeactivated: 'connection.deactivated',
  AccountCreated: 'account.created',
  AccountUpdated: 'account.updated',
  CardholderCreated: 'cardholder.created',
  CardholderUpdated: 'cardholder.updated',
  IssuingRequestCreated: 'issuingRequest.created',
  IssuingRequestUpdated: 'issuingRequest.updated'
} as const;
export type EventEventTypeEnum = typeof EventEventTypeEnum[keyof typeof EventEventTypeEnum];

/**
 *
 * @export
 * @interface FundingSource
 */
export interface FundingSource {
  /**
   *
   * @type {string}
   * @memberof FundingSource
   */
  description: string;
  /**
   *
   * @type {string}
   * @memberof FundingSource
   */
  name: string;
  /**
   *
   * @type {string}
   * @memberof FundingSource
   */
  number: string;
  /**
   *
   * @type {boolean}
   * @memberof FundingSource
   */
  is_verified: boolean;
}
/**
 *
 * @export
 * @interface IssuerCategories
 */
export interface IssuerCategories {
  /**
   * Id of the issuer category. E.g., 5814
   * @type {string}
   * @memberof IssuerCategories
   */
  readonly id?: string;
  /**
   * Name of the issuer category. E.g., Fast Food Restaurants
   * @type {string}
   * @memberof IssuerCategories
   */
  readonly name?: string;
  /**
   * Merchant code of the issuer category. E.g., 5814
   * @type {string}
   * @memberof IssuerCategories
   */
  readonly mcc?: string;
}
/**
 *
 * @export
 * @interface IssuerMap
 */
export interface IssuerMap {
  /**
   * All supported Issuers on the Gateway. Keys are the `IssuerId` that should be used to reference a specific Issuer when an `issuerId` is required for specified API requests.
   * @type {{ [key: string]: IssuerMapIssuersValue; }}
   * @memberof IssuerMap
   */
  readonly issuers?: { [key: string]: IssuerMapIssuersValue };
}
/**
 *
 * @export
 * @interface IssuerMapIssuersValue
 */
export interface IssuerMapIssuersValue {
  /**
   * Official name for data source, e.g. "Gringotts Wizarding Bank".
   * @type {string}
   * @memberof IssuerMapIssuersValue
   */
  readonly name?: string;
  /**
   * URL of issuer logo
   * @type {string}
   * @memberof IssuerMapIssuersValue
   */
  readonly logo?: string;
  /**
   *
   * @type {IssuerMapIssuersValueCapabilities}
   * @memberof IssuerMapIssuersValue
   */
  capabilities?: IssuerMapIssuersValueCapabilities;
}
/**
 * Collection of available capabilities the data source supports.
 * @export
 * @interface IssuerMapIssuersValueCapabilities
 */
export interface IssuerMapIssuersValueCapabilities {
  /**
   * OAuth onboarding is supported.
   * @type {boolean}
   * @memberof IssuerMapIssuersValueCapabilities
   */
  readonly oauth?: boolean;
  /**
   * Some form of card issuing is supported - a combination of physical, ghost, and/or virtual.
   * @type {boolean}
   * @memberof IssuerMapIssuersValueCapabilities
   */
  readonly issuing?: boolean;
  /**
   * Realtime authorization alerting is supported.
   * @type {boolean}
   * @memberof IssuerMapIssuersValueCapabilities
   */
  readonly authorizations?: boolean;
}
/**
 *
 * @export
 * @interface IssuingRequest
 */
export interface IssuingRequest {
  /**
   * Unique ID.
   * @type {string}
   * @memberof IssuingRequest
   */
  readonly id?: string;
  /**
   *
   * @type {IssuingRequestAccount}
   * @memberof IssuingRequest
   */
  account?: IssuingRequestAccount;
  /**
   *
   * @type {IssuingRequestParentCard}
   * @memberof IssuingRequest
   */
  parentCard?: IssuingRequestParentCard | null;
  /**
   *
   * @type {IssuingRequestCardholder}
   * @memberof IssuingRequest
   */
  cardholder?: IssuingRequestCardholder | null;
  /**
   *
   * @type {IssuingRequestCardRequestor}
   * @memberof IssuingRequest
   */
  cardRequestor?: IssuingRequestCardRequestor | null;
  /**
   * Automatically create a Card or VirtualCard from the IssuingRequest if it becomes VALID.
   * @type {boolean}
   * @memberof IssuingRequest
   */
  autoIssue: boolean;
  /**
   *
   * @type {IssuingRequestIssuedCard}
   * @memberof IssuingRequest
   */
  issuedCard?: IssuingRequestIssuedCard;
  /**
   *
   * @type {IssuingRequestIssuedVirtualCard}
   * @memberof IssuingRequest
   */
  issuedVirtualCard?: IssuingRequestIssuedVirtualCard;
  /**
   * The status of the IssuingRequest.
   * @type {string}
   * @memberof IssuingRequest
   */
  readonly status?: IssuingRequestStatusEnum;
  /**
   * Email address of the intended recipient cardholder. This is required for IssuingRequests.
   * @type {string}
   * @memberof IssuingRequest
   */
  email: string;
  /**
   * A description for the target issued object.
   * @type {string}
   * @memberof IssuingRequest
   */
  description?: string;
  /**
   * Indicates if the IssuingRequest is a ghost request (does not exist in external system).
   * @type {boolean}
   * @memberof IssuingRequest
   */
  isGhost?: boolean | null;
  /**
   *
   * @type {IssuingRequestShipping}
   * @memberof IssuingRequest
   */
  shipping?: IssuingRequestShipping | null;
  /**
   *
   * @type {IssuingRequestAllowance}
   * @memberof IssuingRequest
   */
  allowance: IssuingRequestAllowance;
  /**
   * Datetime when the IssuingRequest was created.
   * @type {string}
   * @memberof IssuingRequest
   */
  readonly createdAt?: string;
}

/**
 * @export
 */
export const IssuingRequestStatusEnum = {
  Incomplete: 'incomplete',
  Valid: 'valid',
  Error: 'error',
  Issued: 'issued'
} as const;
export type IssuingRequestStatusEnum = typeof IssuingRequestStatusEnum[keyof typeof IssuingRequestStatusEnum];

/**
 * The Account associated with the IssuingRequest.
 * @export
 * @interface IssuingRequestAccount
 */
export interface IssuingRequestAccount {
  /**
   * Unique ID.
   * @type {string}
   * @memberof IssuingRequestAccount
   */
  id: string;
  /**
   * Issuer-provided unique id.
   * @type {string}
   * @memberof IssuingRequestAccount
   */
  externalId: string;
  /**
   * User-friendly account name.
   * @type {string}
   * @memberof IssuingRequestAccount
   */
  name: string;
  /**
   *
   * @type {CardAccountIssuer}
   * @memberof IssuingRequestAccount
   */
  issuer: CardAccountIssuer | null;
  /**
   * Name of the institution.
   * @type {string}
   * @memberof IssuingRequestAccount
   */
  institutionName: string;
}
/**
 * Allowance details for the IssuingRequest.
 * @export
 * @interface IssuingRequestAllowance
 */
export interface IssuingRequestAllowance {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof IssuingRequestAllowance
   */
  interval: IssuingRequestAllowanceIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof IssuingRequestAllowance
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof IssuingRequestAllowance
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof IssuingRequestAllowance
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof IssuingRequestAllowance
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof IssuingRequestAllowance
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof IssuingRequestAllowance
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof IssuingRequestAllowance
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof IssuingRequestAllowance
   */
  allowedVendor?: string | null;
}

/**
 * @export
 */
export const IssuingRequestAllowanceIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type IssuingRequestAllowanceIntervalEnum =
  typeof IssuingRequestAllowanceIntervalEnum[keyof typeof IssuingRequestAllowanceIntervalEnum];

/**
 * Details of the cardholder who requested the IssuingRequest.
 * @export
 * @interface IssuingRequestCardRequestor
 */
export interface IssuingRequestCardRequestor {
  /**
   * Cardholder ID of the requesting user.
   * @type {string}
   * @memberof IssuingRequestCardRequestor
   */
  cardholderId: string;
  /**
   * Device ID of user requesting the card.
   * @type {string}
   * @memberof IssuingRequestCardRequestor
   */
  deviceId: string;
  /**
   * IP address of user requesting the card.
   * @type {string}
   * @memberof IssuingRequestCardRequestor
   */
  loggedInIpAddress: string;
  /**
   * Session ID of the user requesting the card.
   * @type {string}
   * @memberof IssuingRequestCardRequestor
   */
  sessionId: string;
}
/**
 * The cardholder associated with the IssuingRequest. Request will be unable to be issued if not present.
 * @export
 * @interface IssuingRequestCardholder
 */
export interface IssuingRequestCardholder {
  /**
   * Unique ID.
   * @type {string}
   * @memberof IssuingRequestCardholder
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof IssuingRequestCardholder
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof IssuingRequestCardholder
   */
  firstName?: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof IssuingRequestCardholder
   */
  lastName?: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof IssuingRequestCardholder
   */
  email?: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof IssuingRequestCardholder
   */
  employeeId?: string;
}
/**
 * The card issued as a result of the IssuingRequest.
 * @export
 * @interface IssuingRequestIssuedCard
 */
export interface IssuingRequestIssuedCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof IssuingRequestIssuedCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof IssuingRequestIssuedCard
   */
  externalId?: string | null;
  /**
   *
   * @type {CardDuplicatesInnerCardholder}
   * @memberof IssuingRequestIssuedCard
   */
  cardholder?: CardDuplicatesInnerCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof IssuingRequestIssuedCard
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof IssuingRequestIssuedCard
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof IssuingRequestIssuedCard
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof IssuingRequestIssuedCard
   */
  readonly bin?: string | null;
}
/**
 * The virtual card issued as a result of the IssuingRequest.
 * @export
 * @interface IssuingRequestIssuedVirtualCard
 */
export interface IssuingRequestIssuedVirtualCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof IssuingRequestIssuedVirtualCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof IssuingRequestIssuedVirtualCard
   */
  externalId?: string;
  /**
   *
   * @type {VirtualCardDuplicatesInnerCardholder}
   * @memberof IssuingRequestIssuedVirtualCard
   */
  cardholder?: VirtualCardDuplicatesInnerCardholder;
  /**
   * Date when virtual card will expire
   * @type {string}
   * @memberof IssuingRequestIssuedVirtualCard
   */
  expirationDate: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof IssuingRequestIssuedVirtualCard
   */
  panMasked: string;
  /**
   * Bank Identification Number for virtual card. Generally will be the first four (4) to six (6) digits of the virtual card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof IssuingRequestIssuedVirtualCard
   */
  readonly bin?: string | null;
  /**
   * Card status.
   * @type {string}
   * @memberof IssuingRequestIssuedVirtualCard
   */
  status: IssuingRequestIssuedVirtualCardStatusEnum | null;
}

/**
 * @export
 */
export const IssuingRequestIssuedVirtualCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type IssuingRequestIssuedVirtualCardStatusEnum =
  typeof IssuingRequestIssuedVirtualCardStatusEnum[keyof typeof IssuingRequestIssuedVirtualCardStatusEnum];

/**
 * The parent card associated with the IssuingRequest, if any.
 * @export
 * @interface IssuingRequestParentCard
 */
export interface IssuingRequestParentCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof IssuingRequestParentCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof IssuingRequestParentCard
   */
  externalId?: string | null;
  /**
   *
   * @type {CardDuplicatesInnerCardholder}
   * @memberof IssuingRequestParentCard
   */
  cardholder?: CardDuplicatesInnerCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof IssuingRequestParentCard
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof IssuingRequestParentCard
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof IssuingRequestParentCard
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof IssuingRequestParentCard
   */
  readonly bin?: string | null;
}
/**
 * Shipping and delivery details for the intended Card. Required for physical cards if isGhost is False.
 * @export
 * @interface IssuingRequestShipping
 */
export interface IssuingRequestShipping {
  /**
   *
   * @type {CardDetailShippingAddress}
   * @memberof IssuingRequestShipping
   */
  address: CardDetailShippingAddress;
  /**
   * Shipping service to be used. Is specific to the issuer.
   * @type {string}
   * @memberof IssuingRequestShipping
   */
  service?: IssuingRequestShippingServiceEnum;
  /**
   * Require delivery signature.
   * @type {boolean}
   * @memberof IssuingRequestShipping
   */
  requireSignature: boolean;
  /**
   * Phone number for recipient of shipment.
   * @type {string}
   * @memberof IssuingRequestShipping
   */
  phoneNumber?: string | null;
  /**
   * Shipping carrier.
   * @type {string}
   * @memberof IssuingRequestShipping
   */
  readonly carrier?: string;
  /**
   * Estimated time of arrival.
   * @type {string}
   * @memberof IssuingRequestShipping
   */
  readonly eta?: string;
  /**
   * Shipping status.
   * @type {string}
   * @memberof IssuingRequestShipping
   */
  readonly status?: IssuingRequestShippingStatusEnum;
  /**
   * Shipping tracking number.
   * @type {string}
   * @memberof IssuingRequestShipping
   */
  readonly trackingNumber?: string;
  /**
   * Shipping tracking URL.
   * @type {string}
   * @memberof IssuingRequestShipping
   */
  readonly trackingUrl?: string;
}

/**
 * @export
 */
export const IssuingRequestShippingServiceEnum = {
  Standard: 'Standard',
  Express: 'Express',
  Priority: 'Priority'
} as const;
export type IssuingRequestShippingServiceEnum = typeof IssuingRequestShippingServiceEnum[keyof typeof IssuingRequestShippingServiceEnum];

/**
 * @export
 */
export const IssuingRequestShippingStatusEnum = {
  Pending: 'Pending',
  Shipped: 'Shipped',
  Delivered: 'Delivered',
  Returned: 'Returned',
  Failed: 'Failed',
  Canceled: 'Canceled'
} as const;
export type IssuingRequestShippingStatusEnum = typeof IssuingRequestShippingStatusEnum[keyof typeof IssuingRequestShippingStatusEnum];

/**
 *
 * @export
 * @interface ListAccounts200Response
 */
export interface ListAccounts200Response {
  /**
   *
   * @type {number}
   * @memberof ListAccounts200Response
   */
  count: number;
  /**
   *
   * @type {string}
   * @memberof ListAccounts200Response
   */
  next?: string | null;
  /**
   *
   * @type {string}
   * @memberof ListAccounts200Response
   */
  previous?: string | null;
  /**
   *
   * @type {Array<Account>}
   * @memberof ListAccounts200Response
   */
  results: Array<Account>;
}
/**
 *
 * @export
 * @interface ListCardholders200Response
 */
export interface ListCardholders200Response {
  /**
   *
   * @type {string}
   * @memberof ListCardholders200Response
   */
  next?: string | null;
  /**
   *
   * @type {Array<Cardholder>}
   * @memberof ListCardholders200Response
   */
  results?: Array<Cardholder>;
}
/**
 *
 * @export
 * @interface ListConnections200Response
 */
export interface ListConnections200Response {
  /**
   *
   * @type {Array<ConnectionDetail>}
   * @memberof ListConnections200Response
   */
  results?: Array<ConnectionDetail>;
}
/**
 *
 * @export
 * @interface ListEvents200Response
 */
export interface ListEvents200Response {
  /**
   *
   * @type {string}
   * @memberof ListEvents200Response
   */
  next?: string | null;
  /**
   *
   * @type {Array<Event>}
   * @memberof ListEvents200Response
   */
  results?: Array<Event>;
}
/**
 *
 * @export
 * @interface ListIssuingRequests200Response
 */
export interface ListIssuingRequests200Response {
  /**
   *
   * @type {string}
   * @memberof ListIssuingRequests200Response
   */
  next?: string | null;
  /**
   *
   * @type {Array<IssuingRequest>}
   * @memberof ListIssuingRequests200Response
   */
  results?: Array<IssuingRequest>;
}
/**
 *
 * @export
 * @interface ListVendors200Response
 */
export interface ListVendors200Response {
  /**
   *
   * @type {number}
   * @memberof ListVendors200Response
   */
  count: number;
  /**
   *
   * @type {string}
   * @memberof ListVendors200Response
   */
  next?: string | null;
  /**
   *
   * @type {string}
   * @memberof ListVendors200Response
   */
  previous?: string | null;
  /**
   *
   * @type {Array<Vendor>}
   * @memberof ListVendors200Response
   */
  results: Array<Vendor>;
}
/**
 *
 * @export
 * @interface ListallAuthorizations200Response
 */
export interface ListallAuthorizations200Response {
  /**
   *
   * @type {string}
   * @memberof ListallAuthorizations200Response
   */
  next?: string | null;
  /**
   *
   * @type {Array<Authorization>}
   * @memberof ListallAuthorizations200Response
   */
  results?: Array<Authorization>;
}
/**
 *
 * @export
 * @interface ListallCards200Response
 */
export interface ListallCards200Response {
  /**
   *
   * @type {string}
   * @memberof ListallCards200Response
   */
  next?: string | null;
  /**
   *
   * @type {Array<Card>}
   * @memberof ListallCards200Response
   */
  results?: Array<Card>;
}
/**
 *
 * @export
 * @interface ListallSettlements200Response
 */
export interface ListallSettlements200Response {
  /**
   *
   * @type {string}
   * @memberof ListallSettlements200Response
   */
  next?: string | null;
  /**
   *
   * @type {Array<Settlement>}
   * @memberof ListallSettlements200Response
   */
  results?: Array<Settlement>;
}
/**
 *
 * @export
 * @interface ListallVirtualCards200Response
 */
export interface ListallVirtualCards200Response {
  /**
   *
   * @type {string}
   * @memberof ListallVirtualCards200Response
   */
  next?: string | null;
  /**
   *
   * @type {Array<VirtualCard>}
   * @memberof ListallVirtualCards200Response
   */
  results?: Array<VirtualCard>;
}
/**
 *
 * @export
 * @interface MerchantCategoryCodeGroup
 */
export interface MerchantCategoryCodeGroup {
  /**
   * Name of the merchant category code grouping. E.g., Restaurants
   * @type {string}
   * @memberof MerchantCategoryCodeGroup
   */
  readonly name?: string;
  /**
   * Cards gateway code for the merchant category code grouping. E.g., 5
   * @type {string}
   * @memberof MerchantCategoryCodeGroup
   */
  readonly groupCode?: string;
  /**
   * Description of the merchant category code grouping. E.g., Restaurants and other food vendors
   * @type {string}
   * @memberof MerchantCategoryCodeGroup
   */
  readonly description?: string;
  /**
   *
   * @type {Array<string>}
   * @memberof MerchantCategoryCodeGroup
   */
  readonly mccs?: Array<string>;
}
/**
 *
 * @export
 * @interface PanCard400Response
 */
export interface PanCard400Response {
  /**
   * Descriptive reason why request was unsuccessful.
   * @type {string}
   * @memberof PanCard400Response
   */
  reason?: string;
}
/**
 *
 * @export
 * @interface Session
 */
export interface Session {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Session
   */
  readonly id?: string;
  /**
   * Id of Connection to create Session for
   * @type {string}
   * @memberof Session
   */
  connectionId?: string;
  /**
   * Status representing current state of the onboarding process.
   * @type {string}
   * @memberof Session
   */
  readonly status?: SessionStatusEnum;
  /**
   * Branding to use for UI during onboarding.
   * @type {string}
   * @memberof Session
   */
  branding?: SessionBrandingEnum;
  /**
   * Timestamp after which Session is no longer valid
   * @type {string}
   * @memberof Session
   */
  readonly expiresAt?: string;
  /**
   * URL for start page for onboarding
   * @type {string}
   * @memberof Session
   */
  readonly startPageUrl?: string;
  /**
   * Optional URL of page to direct user to upon onboarding completion or failure
   * @type {string}
   * @memberof Session
   */
  redirectUrl?: string | null;
  /**
   * Business unit the connection belongs to.
   * @type {string}
   * @memberof Session
   */
  readonly businessUnit?: string;
  /**
   * Customer that the connection belongs to (business unit specific).
   * @type {string}
   * @memberof Session
   */
  readonly customerId?: string;
  /**
   *
   * @type {object}
   * @memberof Session
   */
  metadata?: object | null;
}

/**
 * @export
 */
export const SessionStatusEnum = {
  Pending: 'pending',
  Completed: 'completed',
  Failed: 'failed',
  Initialized: 'initialized',
  Connected: 'connected'
} as const;
export type SessionStatusEnum = typeof SessionStatusEnum[keyof typeof SessionStatusEnum];

/**
 * @export
 */
export const SessionBrandingEnum = {
  Emburse: 'emburse',
  Abacus: 'abacus',
  Captio: 'captio',
  Certify: 'certify',
  CertifyAp: 'certify_ap',
  Chromeriver: 'chromeriver',
  EmburseCards: 'emburse_cards',
  EmburseSpend: 'emburse_spend',
  Nexonia: 'nexonia',
  Springahead: 'springahead',
  Tallie: 'tallie'
} as const;
export type SessionBrandingEnum = typeof SessionBrandingEnum[keyof typeof SessionBrandingEnum];

/**
 *
 * @export
 * @interface Settlement
 */
export interface Settlement {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Settlement
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof Settlement
   */
  externalId?: string | null;
  /**
   *
   * @type {SettlementCard}
   * @memberof Settlement
   */
  card: SettlementCard;
  /**
   *
   * @type {AuthorizationMerchant}
   * @memberof Settlement
   */
  merchant: AuthorizationMerchant;
  /**
   *
   * @type {AuthorizationVendor}
   * @memberof Settlement
   */
  vendor: AuthorizationVendor;
  /**
   *
   * @type {SettlementVirtualCard}
   * @memberof Settlement
   */
  virtualCard?: SettlementVirtualCard | null;
  /**
   * Amount settled. Negative for debits and positive for credits.
   * @type {number}
   * @memberof Settlement
   */
  amount: number;
  /**
   * Transaction type.
   * @type {string}
   * @memberof Settlement
   */
  transactionType: SettlementTransactionTypeEnum;
  /**
   * Deprecated -- use transactionType.
   * @type {string}
   * @memberof Settlement
   */
  transaction_type: SettlementTransactionTypeEnum;
  /**
   * A code used to link authorizations and settlements.
   * @type {string}
   * @memberof Settlement
   */
  approvalCode: string;
  /**
   *
   * @type {SettlementAuthorization}
   * @memberof Settlement
   */
  authorization?: SettlementAuthorization;
  /**
   * ISO 4217 currency code for `amount`.
   * @type {string}
   * @memberof Settlement
   */
  currencyCode: string;
  /**
   * Settled amount in original currency (if different from `currencyCode`).
   * @type {number}
   * @memberof Settlement
   */
  foreignAmount?: number | null;
  /**
   * ISO 4217 currecy code for original amount (if different from `currencyCode`).
   * @type {string}
   * @memberof Settlement
   */
  foreignCurrencyCode?: string | null;
  /**
   * True if this settlement represents and account adjustment instead of a purchase. Check the `memo` field for more details if true.
   * @type {boolean}
   * @memberof Settlement
   */
  isAdjustment: boolean;
  /**
   * Additional description of this settlement.
   * @type {string}
   * @memberof Settlement
   */
  memo: string;
  /**
   * Card network.
   * @type {string}
   * @memberof Settlement
   */
  network: SettlementNetworkEnum;
  /**
   * Settlement posting date.
   * @type {string}
   * @memberof Settlement
   */
  postDate: string;
  /**
   * Settlement posting date and time. This field is only provided if both the date and time values are available.
   * @type {string}
   * @memberof Settlement
   */
  postedAt: string;
  /**
   * Date settlement was billed charged.
   * @type {string}
   * @memberof Settlement
   */
  chargeDate?: string | null;
  /**
   * Date settlement was billed or included in a banking statement.
   * @type {string}
   * @memberof Settlement
   */
  billingDate?: string | null;
  /**
   *
   * @type {SettlementLevelThree}
   * @memberof Settlement
   */
  levelThree?: SettlementLevelThree | null;
  /**
   * First instance of this settlement detected (if any) for this customer.
   * @type {string}
   * @memberof Settlement
   */
  readonly duplicateOf?: string | null;
  /**
   * True if settlement is no longer accessible with the issuer. Could be deleted or otherwise unable to sync via gateway
   * @type {boolean}
   * @memberof Settlement
   */
  isPlaceholder: boolean;
  /**
   *
   * @type {SettlementTax}
   * @memberof Settlement
   */
  tax: SettlementTax;
}

/**
 * @export
 */
export const SettlementTransactionTypeEnum = {
  Purchase: 'purchase',
  Payment: 'payment',
  Fee: 'fee',
  Transfer: 'transfer',
  Refund: 'refund',
  Adjustment: 'adjustment',
  Other: 'other',
  Reversal: 'reversal'
} as const;
export type SettlementTransactionTypeEnum = typeof SettlementTransactionTypeEnum[keyof typeof SettlementTransactionTypeEnum];

/**
 * @export
 */
export const SettlementNetworkEnum = {
  Unknown: 'unknown',
  Amex: 'amex',
  Mastercard: 'mastercard',
  Visa: 'visa',
  Demo: 'demo',
  Discover: 'discover',
  DinersClub: 'diners-club'
} as const;
export type SettlementNetworkEnum = typeof SettlementNetworkEnum[keyof typeof SettlementNetworkEnum];

/**
 * Authorization being settled.
 * @export
 * @interface SettlementAuthorization
 */
export interface SettlementAuthorization {
  /**
   * Unique ID.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  externalId?: string | null;
  /**
   * A code used to link authorizations and settlements.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  approvalCode: string;
  /**
   * Approved authorization amount. Negative for debits and positive for credits.
   * @type {number}
   * @memberof SettlementAuthorization
   */
  authorizedAmount: number;
  /**
   * Authorization date.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  chargeDate: string;
  /**
   * Authorization time. This field is only provided if both the date and time at which a transaction was authorized are available.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  chargedAt?: string | null;
  /**
   * ISO 4217 currency code for `authorizedAmount` and `requestedAmount`.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  currencyCode: string;
  /**
   * Amount in original currency (if different from `currencyCode`).
   * @type {number}
   * @memberof SettlementAuthorization
   */
  foreignAmount?: number | null;
  /**
   * ISO 4217 currency code for original amount (if different from `currencyCode`).
   * @type {string}
   * @memberof SettlementAuthorization
   */
  foreignCurrencyCode?: string | null;
  /**
   * Additional description of this authorization.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  memo: string;
  /**
   *
   * @type {AuthorizationMerchant}
   * @memberof SettlementAuthorization
   */
  merchant: AuthorizationMerchant;
  /**
   *
   * @type {AuthorizationVendor}
   * @memberof SettlementAuthorization
   */
  vendor: AuthorizationVendor;
  /**
   * Initial authorization request amount. Negative for debits and positive for credits.
   * @type {number}
   * @memberof SettlementAuthorization
   */
  requestedAmount: number;
  /**
   * Authorization status.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  status: SettlementAuthorizationStatusEnum;
  /**
   * Description for Authorization status. May only populated if status is declined depending on issuer.
   * @type {string}
   * @memberof SettlementAuthorization
   */
  statusDescription: string;
}

/**
 * @export
 */
export const SettlementAuthorizationStatusEnum = {
  Pending: 'pending',
  Closed: 'closed',
  Declined: 'declined'
} as const;
export type SettlementAuthorizationStatusEnum = typeof SettlementAuthorizationStatusEnum[keyof typeof SettlementAuthorizationStatusEnum];

/**
 * Card associated with settlement.
 * @export
 * @interface SettlementCard
 */
export interface SettlementCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof SettlementCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof SettlementCard
   */
  externalId?: string | null;
  /**
   *
   * @type {CardDuplicatesInnerCardholder}
   * @memberof SettlementCard
   */
  cardholder?: CardDuplicatesInnerCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof SettlementCard
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof SettlementCard
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof SettlementCard
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof SettlementCard
   */
  readonly bin?: string | null;
}
/**
 * Level three data associated with settlement.
 * @export
 * @interface SettlementLevelThree
 */
export interface SettlementLevelThree {
  /**
   *
   * @type {SettlementLevelThreeHotel}
   * @memberof SettlementLevelThree
   */
  hotel: SettlementLevelThreeHotel;
  /**
   *
   * @type {SettlementLevelThreeCarRental}
   * @memberof SettlementLevelThree
   */
  carRental: SettlementLevelThreeCarRental;
  /**
   *
   * @type {SettlementLevelThreeFlight}
   * @memberof SettlementLevelThree
   */
  flight: SettlementLevelThreeFlight;
  /**
   *
   * @type {SettlementLevelThreeReceipt}
   * @memberof SettlementLevelThree
   */
  receipt: SettlementLevelThreeReceipt;
  /**
   *
   * @type {SettlementLevelThreeCustomData}
   * @memberof SettlementLevelThree
   */
  customData: SettlementLevelThreeCustomData;
}
/**
 * Detailed car rental information
 * @export
 * @interface SettlementLevelThreeCarRental
 */
export interface SettlementLevelThreeCarRental {
  /**
   * Vehicle Rental Agreement Number
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  rentalAgreementNumber: string;
  /**
   * Name of Person Renting the Vehicle
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  renterName: string;
  /**
   * City where car is scheduled to be returned
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  returnCity: string;
  /**
   * State or Province where Vehicle is scheduled to be returned
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  returnState: string;
  /**
   * Country where Vehicle is scheduled to be returned
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  returnCountry: string;
  /**
   * ID for Location where Vehicle is scheduled to be returned
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  scheduledReturnLocation: string;
  /**
   * Date Vehicle is scheduled to be returned.
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  returnDate: string;
  /**
   * Date of Vehicle rental.
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  checkoutDate: string;
  /**
   * The Customer Service Phone Number of the Card Acceptor
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  customerService800Number: string;
  /**
   * Miles used by the renter/lessee
   * @type {number}
   * @memberof SettlementLevelThreeCarRental
   */
  totalMiles: number;
  /**
   * Indicates the number of maximum free miles for car rental.
   * @type {number}
   * @memberof SettlementLevelThreeCarRental
   */
  maximumFreeMiles: number;
  /**
   * City where the vehicle was rented.
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  checkoutCity: string;
  /**
   * StateProvince where the vehicle was rented.
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  checkoutState: string;
  /**
   * Country where the vehicle was rented.
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  checkoutCountry: string;
  /**
   * Class of Vehicle Rental
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  rentalClass: string;
  /**
   * Indicates the total number of days the vehicle was rented.
   * @type {number}
   * @memberof SettlementLevelThreeCarRental
   */
  daysRented: number;
  /**
   * Location where the rental car was returned
   * @type {string}
   * @memberof SettlementLevelThreeCarRental
   */
  actualReturnLocation: string;
}
/**
 * Detailedf custom data fields
 * @export
 * @interface SettlementLevelThreeCustomData
 */
export interface SettlementLevelThreeCustomData {
  /**
   * Custom data fields
   * @type {Array<SettlementLevelThreeCustomDataLinesInner>}
   * @memberof SettlementLevelThreeCustomData
   */
  readonly lines?: Array<SettlementLevelThreeCustomDataLinesInner>;
}
/**
 *
 * @export
 * @interface SettlementLevelThreeCustomDataLinesInner
 */
export interface SettlementLevelThreeCustomDataLinesInner {
  /**
   * Value of the custom data line
   * @type {string}
   * @memberof SettlementLevelThreeCustomDataLinesInner
   */
  value: string;
  /**
   * Label for the custom data line
   * @type {string}
   * @memberof SettlementLevelThreeCustomDataLinesInner
   */
  label: string;
}
/**
 * Detailed information about a flight ticket with all its legs
 * @export
 * @interface SettlementLevelThreeFlight
 */
export interface SettlementLevelThreeFlight {
  /**
   * Name of the passenger on the flight
   * @type {string}
   * @memberof SettlementLevelThreeFlight
   */
  passengerName: string;
  /**
   * Airport code for the ending destination of the flight
   * @type {string}
   * @memberof SettlementLevelThreeFlight
   */
  finalDestinationCity: string;
  /**
   * Ticket Number printed on boarding pass.
   * @type {string}
   * @memberof SettlementLevelThreeFlight
   */
  ticketNumber: string;
  /**
   *
   * @type {Array<SettlementLevelThreeFlightLegsInner>}
   * @memberof SettlementLevelThreeFlight
   */
  readonly legs?: Array<SettlementLevelThreeFlightLegsInner>;
}
/**
 *
 * @export
 * @interface SettlementLevelThreeFlightLegsInner
 */
export interface SettlementLevelThreeFlightLegsInner {
  /**
   * Sequential numbering of flight legs in chronological order.
   * @type {number}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  tripLegNumber: number;
  /**
   * The time that this leg leaves
   * @type {string}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  departureTime: string;
  /**
   * 3 Character airport code for destination of this leg
   * @type {string}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  destinationCity: string;
  /**
   * Flight number for this leg
   * @type {string}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  flightNumber: string;
  /**
   * 3 character airport code for where this flight begins
   * @type {string}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  originCity: string;
  /**
   * the date of this flight leg
   * @type {string}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  travelDate: string;
  /**
   * 3 digit IATA code for the airline for this flight leg
   * @type {string}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  carrierCode: string;
  /**
   * Airline provided code for the class of service on this flight leg
   * @type {string}
   * @memberof SettlementLevelThreeFlightLegsInner
   */
  serviceClassCode: string;
}
/**
 * Detailed information for a hotel stay
 * @export
 * @interface SettlementLevelThreeHotel
 */
export interface SettlementLevelThreeHotel {
  /**
   * Starting date of hotel stay
   * @type {string}
   * @memberof SettlementLevelThreeHotel
   */
  arrivalDate: string;
  /**
   * Ending date of hotel stay
   * @type {string}
   * @memberof SettlementLevelThreeHotel
   */
  departureDate: string;
  /**
   * Required identifier for the hotel bill
   * @type {string}
   * @memberof SettlementLevelThreeHotel
   */
  folioNumber: string;
  /**
   * Portion of the bill for restaurant charges
   * @type {number}
   * @memberof SettlementLevelThreeHotel
   */
  restaurantChargesAmount: number;
  /**
   * Portion of the bill for the room itself
   * @type {number}
   * @memberof SettlementLevelThreeHotel
   */
  roomRateAmount: number;
  /**
   * Charges for room telephone use
   * @type {number}
   * @memberof SettlementLevelThreeHotel
   */
  telephoneCharges: number;
  /**
   * Amount charged on credit card
   * @type {number}
   * @memberof SettlementLevelThreeHotel
   */
  totalAmountChargedOnCreditCard: number;
  /**
   * Total amount of the hotel bill
   * @type {number}
   * @memberof SettlementLevelThreeHotel
   */
  totalChargesAmount: number;
  /**
   * Portion of the bill for expenses other than the room
   * @type {number}
   * @memberof SettlementLevelThreeHotel
   */
  totalNonRoomChargesAmount: number;
}
/**
 * Detailed receipt information
 * @export
 * @interface SettlementLevelThreeReceipt
 */
export interface SettlementLevelThreeReceipt {
  /**
   *
   * @type {Array<SettlementLevelThreeReceiptLineItemsInner>}
   * @memberof SettlementLevelThreeReceipt
   */
  readonly lineItems?: Array<SettlementLevelThreeReceiptLineItemsInner>;
}
/**
 *
 * @export
 * @interface SettlementLevelThreeReceiptLineItemsInner
 */
export interface SettlementLevelThreeReceiptLineItemsInner {
  /**
   * Identifier for the product
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInner
   */
  productCode: string;
  /**
   * Item description
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInner
   */
  itemDescription: string;
  /**
   * Measurement of amount of item purchased.
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInner
   */
  quantity: number;
  /**
   * Unit that quantity is expressed in
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInner
   */
  unitOfMeasurement: string;
  /**
   * Price for 1 unit (measured per unitOfMeasurement)
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInner
   */
  unitPrice: number;
  /**
   * Quantity multipled by unitPrice
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInner
   */
  extendedItemAmount: number;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTax}
   * @memberof SettlementLevelThreeReceiptLineItemsInner
   */
  tax: SettlementLevelThreeReceiptLineItemsInnerTax;
}
/**
 * Tax data for this line item
 * @export
 * @interface SettlementLevelThreeReceiptLineItemsInnerTax
 */
export interface SettlementLevelThreeReceiptLineItemsInnerTax {
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxTotal}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTax
   */
  total?: SettlementLevelThreeReceiptLineItemsInnerTaxTotal | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxSales}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTax
   */
  sales?: SettlementLevelThreeReceiptLineItemsInnerTaxSales | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxVat}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTax
   */
  vat?: SettlementLevelThreeReceiptLineItemsInnerTaxVat | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTax
   */
  harmonized?: SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxOther}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTax
   */
  other?: SettlementLevelThreeReceiptLineItemsInnerTaxOther | null;
}
/**
 * Harmonized sales tax details for the transaction
 * @export
 * @interface SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized
 */
export interface SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized {
  /**
   * Amount of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized
   */
  amount: number;
  /**
   * Rate of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized
   */
  rate?: number | null;
  /**
   * Note about tax
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized
   */
  note?: string | null;
}
/**
 * Other tax details for the transaction
 * @export
 * @interface SettlementLevelThreeReceiptLineItemsInnerTaxOther
 */
export interface SettlementLevelThreeReceiptLineItemsInnerTaxOther {
  /**
   * Amount of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxOther
   */
  amount: number;
  /**
   * Rate of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxOther
   */
  rate?: number | null;
  /**
   * Note about tax
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxOther
   */
  note?: string | null;
}
/**
 * Sales tax details for the transaction
 * @export
 * @interface SettlementLevelThreeReceiptLineItemsInnerTaxSales
 */
export interface SettlementLevelThreeReceiptLineItemsInnerTaxSales {
  /**
   * Amount of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxSales
   */
  amount: number;
  /**
   * Rate of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxSales
   */
  rate?: number | null;
  /**
   * Note about tax
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxSales
   */
  note?: string | null;
}
/**
 * Total tax details for the transaction
 * @export
 * @interface SettlementLevelThreeReceiptLineItemsInnerTaxTotal
 */
export interface SettlementLevelThreeReceiptLineItemsInnerTaxTotal {
  /**
   * Amount of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxTotal
   */
  amount: number;
  /**
   * Rate of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxTotal
   */
  rate?: number | null;
  /**
   * Note about tax
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxTotal
   */
  note?: string | null;
}
/**
 * VAT tax details for the transaction
 * @export
 * @interface SettlementLevelThreeReceiptLineItemsInnerTaxVat
 */
export interface SettlementLevelThreeReceiptLineItemsInnerTaxVat {
  /**
   * Amount of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxVat
   */
  amount: number;
  /**
   * Rate of tax
   * @type {number}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxVat
   */
  rate?: number | null;
  /**
   * Note about tax
   * @type {string}
   * @memberof SettlementLevelThreeReceiptLineItemsInnerTaxVat
   */
  note?: string | null;
}
/**
 * Tax details for the transaction
 * @export
 * @interface SettlementTax
 */
export interface SettlementTax {
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxTotal}
   * @memberof SettlementTax
   */
  total?: SettlementLevelThreeReceiptLineItemsInnerTaxTotal | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxSales}
   * @memberof SettlementTax
   */
  sales?: SettlementLevelThreeReceiptLineItemsInnerTaxSales | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxVat}
   * @memberof SettlementTax
   */
  vat?: SettlementLevelThreeReceiptLineItemsInnerTaxVat | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized}
   * @memberof SettlementTax
   */
  harmonized?: SettlementLevelThreeReceiptLineItemsInnerTaxHarmonized | null;
  /**
   *
   * @type {SettlementLevelThreeReceiptLineItemsInnerTaxOther}
   * @memberof SettlementTax
   */
  other?: SettlementLevelThreeReceiptLineItemsInnerTaxOther | null;
}
/**
 * Virtual card associated with settlement.
 * @export
 * @interface SettlementVirtualCard
 */
export interface SettlementVirtualCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof SettlementVirtualCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof SettlementVirtualCard
   */
  externalId?: string;
  /**
   *
   * @type {VirtualCardDuplicatesInnerCardholder}
   * @memberof SettlementVirtualCard
   */
  cardholder?: VirtualCardDuplicatesInnerCardholder;
  /**
   * Date when virtual card will expire
   * @type {string}
   * @memberof SettlementVirtualCard
   */
  expirationDate: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof SettlementVirtualCard
   */
  panMasked: string;
  /**
   * Bank Identification Number for virtual card. Generally will be the first four (4) to six (6) digits of the virtual card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof SettlementVirtualCard
   */
  readonly bin?: string | null;
  /**
   * Card status.
   * @type {string}
   * @memberof SettlementVirtualCard
   */
  status: SettlementVirtualCardStatusEnum | null;
}

/**
 * @export
 */
export const SettlementVirtualCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type SettlementVirtualCardStatusEnum = typeof SettlementVirtualCardStatusEnum[keyof typeof SettlementVirtualCardStatusEnum];

/**
 *
 * @export
 * @interface ShippingOption
 */
export interface ShippingOption {
  /**
   * Require signature upon delivery.
   * @type {boolean}
   * @memberof ShippingOption
   */
  requireDeliverySignature: boolean;
  /**
   * Tier of shipping service for delivery.
   * @type {string}
   * @memberof ShippingOption
   */
  service?: ShippingOptionServiceEnum;
  /**
   * Country package will ship from.
   * @type {string}
   * @memberof ShippingOption
   */
  readonly originCountry?: string;
  /**
   * True if destination is within shipping country
   * @type {boolean}
   * @memberof ShippingOption
   */
  readonly destinationWithinOrigin?: boolean;
  /**
   * Estimated minimum number of business days for package to arrive.
   * @type {number}
   * @memberof ShippingOption
   */
  readonly estArrivalMinBusinessDays?: number;
  /**
   * Estimated maximum nunber of business days for package to arrive
   * @type {number}
   * @memberof ShippingOption
   */
  readonly estArrivalMaxBusinessDays?: number;
  /**
   * Base fee amount for shipping service.
   * @type {number}
   * @memberof ShippingOption
   */
  readonly baseFeeAmount?: number;
  /**
   * ISO 4217 currency code for base fee.
   * @type {string}
   * @memberof ShippingOption
   */
  readonly baseFeeCurrency?: string;
  /**
   * Fee amount for requiring a delivery signature.
   * @type {number}
   * @memberof ShippingOption
   */
  readonly requireDeliverySignatureFeeAmount?: number;
  /**
   * ISO 4217 currency code for delivery signature fee.
   * @type {string}
   * @memberof ShippingOption
   */
  readonly requireDeliverySignatureFeeCurrency?: string;
  /**
   * True if a carrier tracking number is available.
   * @type {boolean}
   * @memberof ShippingOption
   */
  readonly hasTrackingNumber?: boolean;
  /**
   * Notes and other additional details regarding the shipping option.
   * @type {string}
   * @memberof ShippingOption
   */
  readonly notes?: string;
}

/**
 * @export
 */
export const ShippingOptionServiceEnum = {
  Standard: 'Standard',
  Express: 'Express',
  Priority: 'Priority'
} as const;
export type ShippingOptionServiceEnum = typeof ShippingOptionServiceEnum[keyof typeof ShippingOptionServiceEnum];

/**
 *
 * @export
 * @interface Signup
 */
export interface Signup {
  /**
   *
   * @type {string}
   * @memberof Signup
   */
  partner: SignupPartnerEnum;
  /**
   *
   * @type {SignupCompany}
   * @memberof Signup
   */
  company: SignupCompany;
  /**
   *
   * @type {SignupRepresentative}
   * @memberof Signup
   */
  representative: SignupRepresentative;
  /**
   *
   * @type {SignupAdmin}
   * @memberof Signup
   */
  admin: SignupAdmin;
  /**
   *
   * @type {SignupAnalytics}
   * @memberof Signup
   */
  analytics?: SignupAnalytics;
  /**
   *
   * @type {string}
   * @memberof Signup
   */
  onboarding_version?: SignupOnboardingVersionEnum;
  /**
   *
   * @type {string}
   * @memberof Signup
   */
  redirect_url?: string;
}

/**
 * @export
 */
export const SignupPartnerEnum = {
  Spend: 'spend'
} as const;
export type SignupPartnerEnum = typeof SignupPartnerEnum[keyof typeof SignupPartnerEnum];

/**
 * @export
 */
export const SignupOnboardingVersionEnum = {
  CardsDefault: 'cards-default',
  Cards2024Q2: 'cards-2024-q2',
  SpendInitial: 'spend-initial',
  Spend2024Q2: 'spend-2024-q2'
} as const;
export type SignupOnboardingVersionEnum = typeof SignupOnboardingVersionEnum[keyof typeof SignupOnboardingVersionEnum];

/**
 *
 * @export
 * @interface SignupAdmin
 */
export interface SignupAdmin {
  /**
   *
   * @type {string}
   * @memberof SignupAdmin
   */
  first_name: string;
  /**
   *
   * @type {string}
   * @memberof SignupAdmin
   */
  last_name: string;
  /**
   *
   * @type {string}
   * @memberof SignupAdmin
   */
  email: string;
  /**
   *
   * @type {string}
   * @memberof SignupAdmin
   */
  phone_number?: string;
}
/**
 *
 * @export
 * @interface SignupAnalytics
 */
export interface SignupAnalytics {
  /**
   *
   * @type {string}
   * @memberof SignupAnalytics
   */
  pendo_account_id: string;
  /**
   *
   * @type {string}
   * @memberof SignupAnalytics
   */
  pendo_visitor_id: string;
}
/**
 *
 * @export
 * @interface SignupCompany
 */
export interface SignupCompany {
  /**
   *
   * @type {string}
   * @memberof SignupCompany
   */
  name: string;
  /**
   *
   * @type {string}
   * @memberof SignupCompany
   */
  legal_name: string;
  /**
   *
   * @type {SignupCompanyAddress}
   * @memberof SignupCompany
   */
  address: SignupCompanyAddress;
  /**
   *
   * @type {string}
   * @memberof SignupCompany
   */
  website: string;
  /**
   *
   * @type {string}
   * @memberof SignupCompany
   */
  phone_number?: string;
}
/**
 *
 * @export
 * @interface SignupCompanyAddress
 */
export interface SignupCompanyAddress {
  /**
   *
   * @type {string}
   * @memberof SignupCompanyAddress
   */
  line_1: string;
  /**
   *
   * @type {string}
   * @memberof SignupCompanyAddress
   */
  city: string;
  /**
   *
   * @type {string}
   * @memberof SignupCompanyAddress
   */
  state: string;
  /**
   *
   * @type {string}
   * @memberof SignupCompanyAddress
   */
  postal_code: string;
}
/**
 *
 * @export
 * @interface SignupRepresentative
 */
export interface SignupRepresentative {
  /**
   *
   * @type {string}
   * @memberof SignupRepresentative
   */
  first_name: string;
  /**
   *
   * @type {string}
   * @memberof SignupRepresentative
   */
  last_name: string;
  /**
   *
   * @type {string}
   * @memberof SignupRepresentative
   */
  email: string;
  /**
   *
   * @type {SignupCompanyAddress}
   * @memberof SignupRepresentative
   */
  address: SignupCompanyAddress;
  /**
   *
   * @type {string}
   * @memberof SignupRepresentative
   */
  birth_date: string;
  /**
   *
   * @type {string}
   * @memberof SignupRepresentative
   */
  phone_number?: string;
}
/**
 *
 * @export
 * @interface UpdateCard
 */
export interface UpdateCard {
  /**
   * status of card. For example active
   * @type {string}
   * @memberof UpdateCard
   */
  status?: UpdateCardStatusEnum;
  /**
   *
   * @type {UpdateCardAllowance}
   * @memberof UpdateCard
   */
  allowance?: UpdateCardAllowance;
}

/**
 * @export
 */
export const UpdateCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type UpdateCardStatusEnum = typeof UpdateCardStatusEnum[keyof typeof UpdateCardStatusEnum];

/**
 * Allowance for card
 * @export
 * @interface UpdateCardAllowance
 */
export interface UpdateCardAllowance {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof UpdateCardAllowance
   */
  interval: UpdateCardAllowanceIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof UpdateCardAllowance
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof UpdateCardAllowance
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof UpdateCardAllowance
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof UpdateCardAllowance
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof UpdateCardAllowance
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof UpdateCardAllowance
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof UpdateCardAllowance
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof UpdateCardAllowance
   */
  allowedVendor?: string | null;
  /**
   * Available amount remaining on the allowance. A `null` value indicates `remainingBalance` not supported by Issuer.
   * @type {number}
   * @memberof UpdateCardAllowance
   */
  readonly remainingBalance?: number | null;
  /**
   * Next date and time when allowance transaction balance will be reset at. This field is based upon the `interval` field. For `continuous` allowance intervals this field will be empty.
   * @type {string}
   * @memberof UpdateCardAllowance
   */
  readonly balanceResetsAt?: string | null;
}

/**
 * @export
 */
export const UpdateCardAllowanceIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type UpdateCardAllowanceIntervalEnum = typeof UpdateCardAllowanceIntervalEnum[keyof typeof UpdateCardAllowanceIntervalEnum];

/**
 *
 * @export
 * @interface UpdateCardholder
 */
export interface UpdateCardholder {
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof UpdateCardholder
   */
  firstName?: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof UpdateCardholder
   */
  lastName?: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof UpdateCardholder
   */
  employeeId?: string;
  /**
   *
   * @type {CardholderDetailPersonalAddress}
   * @memberof UpdateCardholder
   */
  personalAddress?: CardholderDetailPersonalAddress;
  /**
   * Cardholder accepted Terms of Service (ToS) from issuing institution.
   * @type {boolean}
   * @memberof UpdateCardholder
   */
  readonly acceptedToS?: boolean;
  /**
   * Primary phone number of cardholder.
   * @type {string}
   * @memberof UpdateCardholder
   */
  primaryPhoneNumber?: string;
  /**
   * The datetime when cardholder accepted Terms of Service (ToS).
   * @type {string}
   * @memberof UpdateCardholder
   */
  acceptedToSAt?: string;
  /**
   * IP address of logged in cardholder user.
   * @type {string}
   * @memberof UpdateCardholder
   */
  loggedInIpAddress?: string;
}
/**
 *
 * @export
 * @interface UpdateVirtualCard
 */
export interface UpdateVirtualCard {
  /**
   * status of virtual card. For example active
   * @type {string}
   * @memberof UpdateVirtualCard
   */
  status?: UpdateVirtualCardStatusEnum;
  /**
   * Description for virtual card.
   * @type {string}
   * @memberof UpdateVirtualCard
   */
  description?: string;
  /**
   *
   * @type {CreateVirtualCardAllowance}
   * @memberof UpdateVirtualCard
   */
  allowance?: CreateVirtualCardAllowance;
  /**
   *
   * @type {UpdateVirtualCardCardRequestor}
   * @memberof UpdateVirtualCard
   */
  cardRequestor?: UpdateVirtualCardCardRequestor;
}

/**
 * @export
 */
export const UpdateVirtualCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type UpdateVirtualCardStatusEnum = typeof UpdateVirtualCardStatusEnum[keyof typeof UpdateVirtualCardStatusEnum];

/**
 * Details of user requesting the virtual card, used for fraud mitigation
 * @export
 * @interface UpdateVirtualCardCardRequestor
 */
export interface UpdateVirtualCardCardRequestor {
  /**
   * Cardholder ID of the requesting user.
   * @type {string}
   * @memberof UpdateVirtualCardCardRequestor
   */
  cardholderId: string;
  /**
   * Device ID of user requesting the card.
   * @type {string}
   * @memberof UpdateVirtualCardCardRequestor
   */
  deviceId: string;
  /**
   * IP address of user requesting the card.
   * @type {string}
   * @memberof UpdateVirtualCardCardRequestor
   */
  loggedInIpAddress: string;
  /**
   * Session ID of the user requesting the card.
   * @type {string}
   * @memberof UpdateVirtualCardCardRequestor
   */
  sessionId: string;
}
/**
 *
 * @export
 * @interface Vendor
 */
export interface Vendor {
  /**
   * Unique ID.
   * @type {string}
   * @memberof Vendor
   */
  id: string;
  /**
   * Vendor name.
   * @type {string}
   * @memberof Vendor
   */
  name: string;
  /**
   * Vendor website.
   * @type {string}
   * @memberof Vendor
   */
  website: string;
  /**
   * Merchants under the same vendor
   * @type {Array<VendorMerchantsInner>}
   * @memberof Vendor
   */
  merchants: Array<VendorMerchantsInner>;
}
/**
 *
 * @export
 * @interface VendorMerchantsInner
 */
export interface VendorMerchantsInner {
  /**
   * Unique ID.
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  id: string;
  /**
   * Card network accepted by the merchant
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  network: VendorMerchantsInnerNetworkEnum;
  /**
   * Network Id
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  networkId: string;
  /**
   * Network Id
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  mid: string;
  /**
   *
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  name?: string;
  /**
   *
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  address?: string;
  /**
   *
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  city?: string;
  /**
   *
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  state?: string;
  /**
   * Merchant zip code.
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  zipCode: string;
  /**
   *
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  country?: string;
  /**
   *
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  mcc?: string;
  /**
   * Generic merchant category which deviates from standard merchant mcc.
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  categoryDetail: string;
  /**
   * Merchant Tax ID.
   * @type {string}
   * @memberof VendorMerchantsInner
   */
  taxId: string;
}

/**
 * @export
 */
export const VendorMerchantsInnerNetworkEnum = {
  Unknown: 'unknown',
  Amex: 'amex',
  Mastercard: 'mastercard',
  Visa: 'visa',
  Demo: 'demo',
  Discover: 'discover',
  DinersClub: 'diners-club'
} as const;
export type VendorMerchantsInnerNetworkEnum = typeof VendorMerchantsInnerNetworkEnum[keyof typeof VendorMerchantsInnerNetworkEnum];

/**
 *
 * @export
 * @interface VirtualCard
 */
export interface VirtualCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof VirtualCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof VirtualCard
   */
  externalId?: string;
  /**
   *
   * @type {CardCardholder}
   * @memberof VirtualCard
   */
  cardholder: CardCardholder;
  /**
   * Date when virtual card will expire
   * @type {string}
   * @memberof VirtualCard
   */
  expirationDate: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof VirtualCard
   */
  panMasked: string;
  /**
   * Bank Identification Number for virtual card. Generally will be the first four (4) to six (6) digits of the virtual card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof VirtualCard
   */
  readonly bin?: string | null;
  /**
   * Card status.
   * @type {string}
   * @memberof VirtualCard
   */
  status: VirtualCardStatusEnum | null;
  /**
   *
   * @type {VirtualCardAccount}
   * @memberof VirtualCard
   */
  account: VirtualCardAccount;
  /**
   *
   * @type {VirtualCardCard}
   * @memberof VirtualCard
   */
  card: VirtualCardCard;
  /**
   * Card network.
   * @type {string}
   * @memberof VirtualCard
   */
  network: VirtualCardNetworkEnum;
  /**
   * Description for virtual card
   * @type {string}
   * @memberof VirtualCard
   */
  description?: string | null;
  /**
   *
   * @type {VirtualCardAllowance}
   * @memberof VirtualCard
   */
  allowance: VirtualCardAllowance | null;
  /**
   * Gateway virtual cards in other connections for the customer with same externalId
   * @type {Array<VirtualCardDuplicatesInner>}
   * @memberof VirtualCard
   */
  readonly duplicates?: Array<VirtualCardDuplicatesInner>;
}

/**
 * @export
 */
export const VirtualCardStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type VirtualCardStatusEnum = typeof VirtualCardStatusEnum[keyof typeof VirtualCardStatusEnum];

/**
 * @export
 */
export const VirtualCardNetworkEnum = {
  Unknown: 'unknown',
  Amex: 'amex',
  Mastercard: 'mastercard',
  Visa: 'visa',
  Demo: 'demo',
  Discover: 'discover',
  DinersClub: 'diners-club'
} as const;
export type VirtualCardNetworkEnum = typeof VirtualCardNetworkEnum[keyof typeof VirtualCardNetworkEnum];

/**
 * Parent account of the virtual card
 * @export
 * @interface VirtualCardAccount
 */
export interface VirtualCardAccount {
  /**
   * Unique ID.
   * @type {string}
   * @memberof VirtualCardAccount
   */
  id: string;
  /**
   * Issuer-provided unique id.
   * @type {string}
   * @memberof VirtualCardAccount
   */
  externalId: string;
  /**
   * User-friendly account name.
   * @type {string}
   * @memberof VirtualCardAccount
   */
  name: string;
  /**
   *
   * @type {CardAccountIssuer}
   * @memberof VirtualCardAccount
   */
  issuer: CardAccountIssuer | null;
  /**
   * Name of the institution.
   * @type {string}
   * @memberof VirtualCardAccount
   */
  institutionName: string;
}
/**
 * The allowance associated with the virtual card
 * @export
 * @interface VirtualCardAllowance
 */
export interface VirtualCardAllowance {
  /**
   * Interval for cumulative limits. Ex: weekly.
   * @type {string}
   * @memberof VirtualCardAllowance
   */
  interval: VirtualCardAllowanceIntervalEnum;
  /**
   * Max cumulative dollar amount allowed for specified interval of time.
   * @type {number}
   * @memberof VirtualCardAllowance
   */
  intervalAmount: number;
  /**
   * Max dollar amount for a single transaction. Value will default to $10,000 for some issuers.
   * @type {number}
   * @memberof VirtualCardAllowance
   */
  transactionLimit?: number | null;
  /**
   * Starting date inclusive of when allowance will be active.
   * @type {string}
   * @memberof VirtualCardAllowance
   */
  startDate?: string | null;
  /**
   * Ending date inclusive of when allowance will be active.
   * @type {string}
   * @memberof VirtualCardAllowance
   */
  endDate?: string | null;
  /**
   * List of allowed merchant categories or null for unset/any. All categories needs to be passed in. Categories from previous request are not retained.
   * @type {Array<string>}
   * @memberof VirtualCardAllowance
   */
  allowedCategories?: Array<string> | null;
  /**
   * List of allowed merchant category groups or null for unset/any. All category group needs to be passed in. Category group from previous request are not retained.
   * @type {Array<string>}
   * @memberof VirtualCardAllowance
   */
  allowedCategoryGroups?: Array<string> | null;
  /**
   * List of allowed merchant ids or null for unset/any. All merchantIds needs to be passed in. MerchantIds from previous request are not retained.
   * @type {Array<string>}
   * @memberof VirtualCardAllowance
   */
  allowedMerchantIds?: Array<string> | null;
  /**
   * Vendor id or null for unset/any.
   * @type {string}
   * @memberof VirtualCardAllowance
   */
  allowedVendor?: string | null;
  /**
   * Available amount remaining on the allowance. A `null` value indicates `remainingBalance` not supported by Issuer.
   * @type {number}
   * @memberof VirtualCardAllowance
   */
  readonly remainingBalance?: number | null;
  /**
   * Next date and time when allowance transaction balance will be reset at. This field is based upon the `interval` field. For `continuous` allowance intervals this field will be empty.
   * @type {string}
   * @memberof VirtualCardAllowance
   */
  readonly balanceResetsAt?: string | null;
}

/**
 * @export
 */
export const VirtualCardAllowanceIntervalEnum = {
  Daily: 'daily',
  Weekly: 'weekly',
  Monthly: 'monthly',
  Continuous: 'continuous'
} as const;
export type VirtualCardAllowanceIntervalEnum = typeof VirtualCardAllowanceIntervalEnum[keyof typeof VirtualCardAllowanceIntervalEnum];

/**
 * Card this virtual card stems from
 * @export
 * @interface VirtualCardCard
 */
export interface VirtualCardCard {
  /**
   * Unique ID.
   * @type {string}
   * @memberof VirtualCardCard
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof VirtualCardCard
   */
  externalId?: string | null;
  /**
   *
   * @type {CardDuplicatesInnerCardholder}
   * @memberof VirtualCardCard
   */
  cardholder?: CardDuplicatesInnerCardholder;
  /**
   * Card expiration date.
   * @type {string}
   * @memberof VirtualCardCard
   */
  expirationDate?: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof VirtualCardCard
   */
  readonly panMasked?: string;
  /**
   * ISO 4217 currency code for settled transactions.
   * @type {string}
   * @memberof VirtualCardCard
   */
  readonly currencyCode?: string;
  /**
   * Bank Identification Number for card. Generally will be the first four (4) to six (6) digits of the card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof VirtualCardCard
   */
  readonly bin?: string | null;
}
/**
 *
 * @export
 * @interface VirtualCardDetail
 */
export interface VirtualCardDetail {
  /**
   * Unique ID.
   * @type {string}
   * @memberof VirtualCardDetail
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof VirtualCardDetail
   */
  externalId?: string;
  /**
   *
   * @type {CardCardholder}
   * @memberof VirtualCardDetail
   */
  cardholder: CardCardholder;
  /**
   * Date when virtual card will expire
   * @type {string}
   * @memberof VirtualCardDetail
   */
  expirationDate: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof VirtualCardDetail
   */
  panMasked: string;
  /**
   * Bank Identification Number for virtual card. Generally will be the first four (4) to six (6) digits of the virtual card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof VirtualCardDetail
   */
  readonly bin?: string | null;
  /**
   * Card status.
   * @type {string}
   * @memberof VirtualCardDetail
   */
  status: VirtualCardDetailStatusEnum | null;
  /**
   *
   * @type {VirtualCardAccount}
   * @memberof VirtualCardDetail
   */
  account: VirtualCardAccount;
  /**
   *
   * @type {VirtualCardCard}
   * @memberof VirtualCardDetail
   */
  card: VirtualCardCard;
  /**
   * Card network.
   * @type {string}
   * @memberof VirtualCardDetail
   */
  network: VirtualCardDetailNetworkEnum;
  /**
   * Description for virtual card
   * @type {string}
   * @memberof VirtualCardDetail
   */
  description?: string | null;
  /**
   *
   * @type {VirtualCardAllowance}
   * @memberof VirtualCardDetail
   */
  allowance: VirtualCardAllowance | null;
  /**
   * Gateway virtual cards in other connections for the customer with same externalId
   * @type {Array<VirtualCardDuplicatesInner>}
   * @memberof VirtualCardDetail
   */
  readonly duplicates?: Array<VirtualCardDuplicatesInner>;
  /**
   *
   * @type {VirtualCardDetailBillingAddress}
   * @memberof VirtualCardDetail
   */
  billingAddress?: VirtualCardDetailBillingAddress;
}

/**
 * @export
 */
export const VirtualCardDetailStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type VirtualCardDetailStatusEnum = typeof VirtualCardDetailStatusEnum[keyof typeof VirtualCardDetailStatusEnum];

/**
 * @export
 */
export const VirtualCardDetailNetworkEnum = {
  Unknown: 'unknown',
  Amex: 'amex',
  Mastercard: 'mastercard',
  Visa: 'visa',
  Demo: 'demo',
  Discover: 'discover',
  DinersClub: 'diners-club'
} as const;
export type VirtualCardDetailNetworkEnum = typeof VirtualCardDetailNetworkEnum[keyof typeof VirtualCardDetailNetworkEnum];

/**
 * The billing address associated with the parent card
 * @export
 * @interface VirtualCardDetailBillingAddress
 */
export interface VirtualCardDetailBillingAddress {
  /**
   * Person or organization located at address.
   * @type {string}
   * @memberof VirtualCardDetailBillingAddress
   */
  attn?: string | null;
  /**
   * First line of address. E.g. 123 Fake Street
   * @type {string}
   * @memberof VirtualCardDetailBillingAddress
   */
  address1: string;
  /**
   * Second line of address. E.g. Apartment 312
   * @type {string}
   * @memberof VirtualCardDetailBillingAddress
   */
  address2?: string | null;
  /**
   * City of address
   * @type {string}
   * @memberof VirtualCardDetailBillingAddress
   */
  city: string;
  /**
   * US state or territory of address
   * @type {string}
   * @memberof VirtualCardDetailBillingAddress
   */
  state: string;
  /**
   * Country of address
   * @type {string}
   * @memberof VirtualCardDetailBillingAddress
   */
  country?: string;
  /**
   * Zipcode of address
   * @type {string}
   * @memberof VirtualCardDetailBillingAddress
   */
  zipCode: string;
}
/**
 *
 * @export
 * @interface VirtualCardDuplicatesInner
 */
export interface VirtualCardDuplicatesInner {
  /**
   * Unique ID.
   * @type {string}
   * @memberof VirtualCardDuplicatesInner
   */
  id: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof VirtualCardDuplicatesInner
   */
  externalId?: string;
  /**
   *
   * @type {VirtualCardDuplicatesInnerCardholder}
   * @memberof VirtualCardDuplicatesInner
   */
  cardholder?: VirtualCardDuplicatesInnerCardholder;
  /**
   * Date when virtual card will expire
   * @type {string}
   * @memberof VirtualCardDuplicatesInner
   */
  expirationDate: string | null;
  /**
   * Redacted card number. VISA and MasterCard numbers have sixteen digits with the last four unredacted. Amex numbers have fifteen digits with the last five unredacted.
   * @type {string}
   * @memberof VirtualCardDuplicatesInner
   */
  panMasked: string;
  /**
   * Bank Identification Number for virtual card. Generally will be the first four (4) to six (6) digits of the virtual card's PAN depending on the issuing bank and card network.
   * @type {string}
   * @memberof VirtualCardDuplicatesInner
   */
  readonly bin?: string | null;
  /**
   * Card status.
   * @type {string}
   * @memberof VirtualCardDuplicatesInner
   */
  status: VirtualCardDuplicatesInnerStatusEnum | null;
}

/**
 * @export
 */
export const VirtualCardDuplicatesInnerStatusEnum = {
  Unactivated: 'unactivated',
  Active: 'active',
  Suspended: 'suspended',
  Canceled: 'canceled',
  Pending: 'pending',
  LostOrStolen: 'lost_or_stolen'
} as const;
export type VirtualCardDuplicatesInnerStatusEnum =
  typeof VirtualCardDuplicatesInnerStatusEnum[keyof typeof VirtualCardDuplicatesInnerStatusEnum];

/**
 * Cardholder for VirtualCard.
 * @export
 * @interface VirtualCardDuplicatesInnerCardholder
 */
export interface VirtualCardDuplicatesInnerCardholder {
  /**
   * Unique ID.
   * @type {string}
   * @memberof VirtualCardDuplicatesInnerCardholder
   */
  id?: string;
  /**
   * Issuer-provided unique ID.
   * @type {string}
   * @memberof VirtualCardDuplicatesInnerCardholder
   */
  readonly externalId?: string | null;
  /**
   * First name of cardholder.
   * @type {string}
   * @memberof VirtualCardDuplicatesInnerCardholder
   */
  firstName: string;
  /**
   * Last name of cardholder.
   * @type {string}
   * @memberof VirtualCardDuplicatesInnerCardholder
   */
  lastName: string;
  /**
   * Email address of cardholder.
   * @type {string}
   * @memberof VirtualCardDuplicatesInnerCardholder
   */
  email: string;
  /**
   * Employee id of cardholder.
   * @type {string}
   * @memberof VirtualCardDuplicatesInnerCardholder
   */
  employeeId?: string;
}
